import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import Orderbook0dColumnCharts from '../components/Orderbook0dColumnCharts';
import './Dashboard.css';
import './AnalyPage.css';

const DELAY_BUY_THRESHOLD_MS = 10 * 1000;
const SLIPPAGE_THRESHOLD_PCT = 0.1;

const AnalyPage: React.FC = () => {
  const [selectedTimeRange, setSelectedTimeRange] = useState<string>('');
  const [importHistory, setImportHistory] = useState<Array<{
    id: number;
    startTime: string;
    endTime: string;
    label: string;
    importTime: string;
    sequenceNumber: number;
    isSelected: boolean;
  }>>([]);

  const [detailStockCode, setDetailStockCode] = useState<string>('');
  const [detailExtractLoading, setDetailExtractLoading] = useState<boolean>(false);
  const [extractingStockCode, setExtractingStockCode] = useState<string>('');
  const [detailExtractError, setDetailExtractError] = useState<string | null>(null);
  const [detailExtractMessage, setDetailExtractMessage] = useState<string | null>(null);
  const [savedDetailItems, setSavedDetailItems] = useState<Array<{ redis_key: string; stock_code: string; stock_name?: string }>>([]);
  const [savedDetailKeysLoading, setSavedDetailKeysLoading] = useState<boolean>(false);
  /** 차트별 로드: base 키(종목 클릭 시 설정), 섹션 열 때 해당 redis_key만 조회 */
  const [detailViewBaseKey, setDetailViewBaseKey] = useState<string | null>(null);
  const [detailViewBySection, setDetailViewBySection] = useState<Record<string, Array<{ t: number; src?: string; data: Record<string, unknown> }>>>({});
  const [detailViewLoadingSections, setDetailViewLoadingSections] = useState<Set<string>>(new Set());
  /** 이번에 로드할 섹션 ID 목록(진행률 계산용). onNeedSections 콜백으로 설정 */
  const [detailViewRequestedSectionIds, setDetailViewRequestedSectionIds] = useState<string[]>([]);
  const [detailViewError, setDetailViewError] = useState<string | null>(null);

  /** 백테스트 일일 매매 결과 (bt_result_day_trading) */
  const [btResultDayTrading, setBtResultDayTrading] = useState<Array<Record<string, unknown>>>([]);
  const [btResultDayTradingLoading, setBtResultDayTradingLoading] = useState(true);
  /** 실제 매매 기록 (bt_actual_trade_stock) - 백테스트 결과와 비교용 */
  const [actualTrades, setActualTrades] = useState<Array<{ trade_type: string; stock_code: string; stock_name?: string; price: number; order_price?: string; change_rate?: number; collected_at: string; trade_timestamp?: string; fill_timestamp?: string; error_reason?: string; trade_reason?: string }>>([]);
  /** 백테 회차별 거래시도 기록 (bt_trade_stock, error_reason + trade_reason 포함). round → TradeStockRecord[] */
  type BtAttempt = { trade_type: string; stock_code: string; stock_name?: string; price: number; order_price?: string; change_rate?: number; collected_at: string; trade_timestamp?: string; fill_timestamp?: string; error_reason?: string; trade_reason?: string };
  const [btAttemptsByRound, setBtAttemptsByRound] = useState<Map<number, BtAttempt[]>>(new Map());
  /** 종목(행) 페이지네이션: 한 페이지에 종목 30개. 매수/매도 시도(컬럼)는 회차 표 자체의 가로 스크롤로 모두 노출. */
  const STOCK_PAGE_SIZE = 5;
  const [stockPage, setStockPage] = useState(0);
  /** 키움 실제 체결내역 (kiwoom_order_history, kt00007) - 브로커 측 체결 기록 */
  const [kiwoomRecords, setKiwoomRecords] = useState<Array<{ id: number; ord_dt: string; ord_tm: string; stk_cd: string; stk_nm: string; io_tp_nm: string; cntr_qty: string; cntr_uv: string; ord_uv: string; comm_ord_tp: string }>>([]);
  /** 회차 삭제 시 UI에서만 제외 (키 = session_id/round 식별자) */
  const [deletedRoundKeys] = useState<string[]>([]);
  /** 삭제 확인 모달: 삭제 대상 회차 (round=글로벌, displayRound=구간 내 순번) */
  const [roundToDelete, setRoundToDelete] = useState<{ round: number; displayRound: number } | null>(null);
  const [deleteRoundLoading, setDeleteRoundLoading] = useState(false);
  const [deleteRoundError, setDeleteRoundError] = useState<string | null>(null);
  /** 백테스트 테이블에서 종목 클릭 시 해당 종목의 매매 수치 (flu_rt 차트 아래 표시용) */
  const [btDetailTrades, setBtDetailTrades] = useState<Array<{ buy_rate?: unknown; sell_rate?: unknown; buy_tick_id?: unknown; sell_tick_id?: unknown; profit_rate?: unknown; displayRound?: number; firstCreatedAt?: string }> | null>(null);
  /** 회차별 종목 메모 (키: stockCode_groupKey) - MongoDB bt_result_memos 컬렉션 */
  const [btResultMemos, setBtResultMemos] = useState<Record<string, string>>({});
  const [, setBtResultMemosLoading] = useState(false);
  const [lastMemoSavedAt, setLastMemoSavedAt] = useState<string | null>(null);

  const fetchBtResultMemos = useCallback(async () => {
    setBtResultMemosLoading(true);
    try {
      const res = await fetch('/mercury-backend/bt/result-day-trading-memos');
      const data = await res.json().catch(() => ({}));
      const memos = data?.data ?? {};
      setBtResultMemos(typeof memos === 'object' ? memos : {});
    } catch {
      setBtResultMemos({});
    } finally {
      setBtResultMemosLoading(false);
    }
  }, []);

  const memoSaveTimersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const setBtResultMemo = useCallback((key: string, value: string) => {
    let stockCode: string;
    let groupKey: string;
    if (key.startsWith('table_bottom_')) {
      stockCode = 'table_bottom';
      groupKey = key.slice(13);
    } else {
      const idx = key.indexOf('_');
      stockCode = idx >= 0 ? key.slice(0, idx) : key;
      groupKey = idx >= 0 ? key.slice(idx + 1) : '';
    }
    if (!stockCode || !groupKey) return;
    setBtResultMemos((prev) => ({ ...prev, [key]: value }));
    const prev = memoSaveTimersRef.current[key];
    if (prev) clearTimeout(prev);
    memoSaveTimersRef.current[key] = setTimeout(() => {
      delete memoSaveTimersRef.current[key];
      fetch('/mercury-backend/bt/result-day-trading-memo', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stock_code: stockCode, group_key: groupKey, memo: value }),
      }).then((res) => {
        if (res.ok) {
          const now = new Date();
          setLastMemoSavedAt(now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0') + '-' + String(now.getDate()).padStart(2, '0') + ' ' + String(now.getHours()).padStart(2, '0') + ':' + String(now.getMinutes()).padStart(2, '0') + ':' + String(now.getSeconds()).padStart(2, '0'));
        }
      }).catch(() => { /* ignore */ });
    }, 400);
  }, []);

  const fetchBtResultDayTrading = useCallback(async () => {
    setBtResultDayTradingLoading(true);
    try {
      const res = await fetch('/mercury-backend/bt/result-day-trading?limit=5000');
      const data = await res.json().catch(() => ({}));
      const items = data?.data ?? data?.items ?? [];
      setBtResultDayTrading(Array.isArray(items) ? items : []);
    } catch {
      setBtResultDayTrading([]);
    } finally {
      setBtResultDayTradingLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBtResultDayTrading();
  }, [fetchBtResultDayTrading]);

  useEffect(() => {
    fetchBtResultMemos();
  }, [fetchBtResultMemos]);

  /** 선택된 시간 범위에 해당하는 item (startTime, endTime) */
  const selectedTimeRangeItem = React.useMemo(() => {
    if (!selectedTimeRange || importHistory.length === 0) return null;
    return importHistory.find((h) => h.id.toString() === selectedTimeRange) ?? null;
  }, [selectedTimeRange, importHistory]);

  /** 선택된 시간 범위에 해당하는 round 의 거래시도(bt_trade_stock)만 조회 — 거부 사유 포함 */
  useEffect(() => {
    const rounds = new Set<number>();

    let st = selectedTimeRangeItem?.startTime ?? '';
    let et = selectedTimeRangeItem?.endTime ?? '';
    if (st.includes('T') && !st.includes('Z') && !st.match(/[+-]\d{2}:\d{2}$/)) st = st + 'Z';
    if (et.includes('T') && !et.includes('Z') && !et.match(/[+-]\d{2}:\d{2}$/)) et = et + 'Z';
    const startMs = Date.parse(st);
    const endMs = Date.parse(et);
    const hasValidRange = !Number.isNaN(startMs) && !Number.isNaN(endMs);

    for (const row of btResultDayTrading) {
      const r = Number(row.round ?? 0);
      if (r <= 0) continue;
      if (hasValidRange) {
        const tickStr = String(row.buy_tick_id ?? row.created_at ?? row.collected_at ?? '');
        if (!tickStr) continue;
        let normalized = tickStr.replace(' ', 'T').trim();
        if (!normalized.endsWith('Z') && !normalized.match(/[+-]\d{2}:\d{2}$/)) normalized = normalized + 'Z';
        const rowMs = Date.parse(normalized);
        if (!Number.isNaN(rowMs) && rowMs >= startMs && rowMs <= endMs) rounds.add(r);
      } else {
        rounds.add(r);
      }
    }
    if (rounds.size === 0) {
      setBtAttemptsByRound(new Map());
      return;
    }
    let cancelled = false;
    (async () => {
      const next = new Map<number, BtAttempt[]>();
      for (const r of Array.from(rounds)) {
        try {
          const res = await fetch(`/mercury-backend/bt/bt-trade-stock-by-round?round=${r}`);
          const data = await res.json().catch(() => ({}));
          const items = Array.isArray(data?.data) ? data.data : [];
          next.set(r, items as BtAttempt[]);
        } catch {
          next.set(r, []);
        }
      }
      if (!cancelled) setBtAttemptsByRound(next);
    })();
    return () => { cancelled = true; };
  }, [btResultDayTrading, selectedTimeRangeItem]);

  useEffect(() => () => {
    Object.values(memoSaveTimersRef.current).forEach(clearTimeout);
    memoSaveTimersRef.current = {};
  }, []);

  /** 선택 구간이 바뀌면 실제 매매 기록(bt_actual_trade_stock) 조회 */
  useEffect(() => {
    const start = selectedTimeRangeItem?.startTime;
    const end = selectedTimeRangeItem?.endTime;
    if (!start || !end) {
      setActualTrades([]);
      return;
    }
    let st = start;
    let et = end;
    if (st.includes('T') && !st.includes('Z') && !st.match(/[+-]\d{2}:\d{2}$/)) st = st + 'Z';
    if (et.includes('T') && !et.includes('Z') && !et.match(/[+-]\d{2}:\d{2}$/)) et = et + 'Z';
    const url = `/mercury-backend/bt/actual-trade-stock?start_time=${encodeURIComponent(st)}&end_time=${encodeURIComponent(et)}`;
    let cancelled = false;
    fetch(url)
      .then((r) => r.json().catch(() => ({})))
      .then((data) => {
        if (cancelled) return;
        const items = data?.data ?? [];
        setActualTrades(Array.isArray(items) ? items : []);
      })
      .catch(() => {
        if (!cancelled) setActualTrades([]);
      });
    return () => { cancelled = true; };
  }, [selectedTimeRangeItem?.startTime, selectedTimeRangeItem?.endTime]);

  /** 선택 구간이 바뀌면 키움 체결내역(kiwoom_order_history, kt00007) 조회 — KST 일자 리스트로 백엔드에 요청 */
  useEffect(() => {
    const start = selectedTimeRangeItem?.startTime;
    const end = selectedTimeRangeItem?.endTime;
    if (!start || !end) {
      setKiwoomRecords([]);
      return;
    }
    const ensureZ = (s: string) =>
      s && !s.endsWith('Z') && !/[+-]\d{2}:\d{2}$/.test(s) ? s + 'Z' : s;
    const sd = new Date(ensureZ(start));
    const ed = new Date(ensureZ(end));
    if (Number.isNaN(sd.getTime()) || Number.isNaN(ed.getTime()) || sd > ed) {
      setKiwoomRecords([]);
      return;
    }
    const fmt = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Seoul', year: 'numeric', month: '2-digit', day: '2-digit' });
    const dates: string[] = [];
    const cur = new Date(sd);
    for (let i = 0; i < 31 && cur <= ed; i++) {
      const ymd = fmt.format(cur).replace(/-/g, '');
      if (!dates.includes(ymd)) dates.push(ymd);
      cur.setUTCDate(cur.getUTCDate() + 1);
    }
    const lastYmd = fmt.format(ed).replace(/-/g, '');
    if (!dates.includes(lastYmd)) dates.push(lastYmd);
    if (dates.length === 0) {
      setKiwoomRecords([]);
      return;
    }
    let cancelled = false;
    fetch(`/mercury-backend/bt/kiwoom-trading-history?dates=${dates.join(',')}`)
      .then((r) => r.json().catch(() => ({})))
      .then((data) => {
        if (cancelled) return;
        const items = Array.isArray(data?.data) ? data.data : [];
        // UTC 시간 범위 내로 추가 필터링 (일자 단위가 아닌 분 단위 정확도)
        const startMs = sd.getTime();
        const endMs = ed.getTime();
        const filtered = items.filter((r: { ord_dt?: string; ord_tm?: string }) => {
          if (!r.ord_dt || r.ord_dt.length !== 8 || !r.ord_tm) return false;
          const ymd = `${r.ord_dt.slice(0, 4)}-${r.ord_dt.slice(4, 6)}-${r.ord_dt.slice(6, 8)}`;
          const t = Date.parse(`${ymd}T${r.ord_tm}+09:00`);
          return Number.isFinite(t) && t >= startMs && t <= endMs;
        });
        setKiwoomRecords(filtered);
      })
      .catch(() => {
        if (!cancelled) setKiwoomRecords([]);
      });
    return () => { cancelled = true; };
  }, [selectedTimeRangeItem?.startTime, selectedTimeRangeItem?.endTime]);

  /** 종목코드 → 실제 매매 기록 목록 (시간순) */
  const actualTradesByStock = React.useMemo(() => {
    const map = new Map<string, Array<{ trade_type: string; price: number; order_price?: string; change_rate?: number; collected_at: string; trade_timestamp?: string; fill_timestamp?: string; error_reason?: string; trade_reason?: string }>>();
    for (const t of actualTrades) {
      const sc = String(t.stock_code ?? '');
      if (!sc) continue;
      if (!map.has(sc)) map.set(sc, []);
      map.get(sc)!.push({ trade_type: t.trade_type, price: t.price, order_price: t.order_price, change_rate: t.change_rate, collected_at: t.collected_at, trade_timestamp: t.trade_timestamp, fill_timestamp: t.fill_timestamp, error_reason: t.error_reason, trade_reason: t.trade_reason });
    }
    for (const list of Array.from(map.values())) {
      list.sort((a, b) => (a.trade_timestamp ?? a.collected_at).localeCompare(b.trade_timestamp ?? b.collected_at));
    }
    return map;
  }, [actualTrades]);

  /** 종목코드 → 매수/매도 페어 목록. BUY 다음에 오는 SELL과 매칭. 짝없는 경우는 한쪽만 채움. */
  type ActualTrade = { price: number; order_price?: string; change_rate?: number; collected_at: string; trade_timestamp?: string; fill_timestamp?: string; error_reason?: string; trade_reason?: string };
  type ActualPair = {
    buy?: ActualTrade;
    sell?: ActualTrade;
  };
  const actualTradesPairsByStock = React.useMemo(() => {
    const result = new Map<string, ActualPair[]>();
    for (const [stockCode, trades] of Array.from(actualTradesByStock.entries())) {
      const pairs: ActualPair[] = [];
      let pendingBuy: ActualTrade | null = null;
      for (const t of trades) {
        const isBuy = String(t.trade_type).toUpperCase() === 'BUY';
        const trade: ActualTrade = { price: t.price, order_price: t.order_price, change_rate: t.change_rate, collected_at: t.collected_at, trade_timestamp: t.trade_timestamp, fill_timestamp: t.fill_timestamp, error_reason: t.error_reason, trade_reason: t.trade_reason };
        if (isBuy) {
          if (pendingBuy) pairs.push({ buy: pendingBuy });
          pendingBuy = trade;
        } else {
          if (pendingBuy) {
            pairs.push({ buy: pendingBuy, sell: trade });
            pendingBuy = null;
          } else {
            pairs.push({ sell: trade });
          }
        }
      }
      if (pendingBuy) pairs.push({ buy: pendingBuy });
      result.set(stockCode, pairs);
    }
    return result;
  }, [actualTradesByStock]);

  /** 백테 회차별 거래시도 페어 — round → (stock → ActualPair[]). actualTradesPairsByStock와 동일 로직. */
  const btAttemptPairsByRound = React.useMemo(() => {
    const out = new Map<number, Map<string, ActualPair[]>>();
    for (const [round, attempts] of Array.from(btAttemptsByRound.entries())) {
      const byStock = new Map<string, BtAttempt[]>();
      for (const a of attempts) {
        const sc = String(a.stock_code ?? '');
        if (!sc) continue;
        if (!byStock.has(sc)) byStock.set(sc, []);
        byStock.get(sc)!.push(a);
      }
      const pairsByStock = new Map<string, ActualPair[]>();
      for (const [sc, list] of Array.from(byStock.entries())) {
        list.sort((a, b) => (a.trade_timestamp ?? a.collected_at).localeCompare(b.trade_timestamp ?? b.collected_at));
        const pairs: ActualPair[] = [];
        let pendingBuy: ActualTrade | null = null;
        for (const t of list) {
          const isBuy = String(t.trade_type).toUpperCase() === 'BUY';
          const trade: ActualTrade = { price: t.price, order_price: t.order_price, change_rate: t.change_rate, collected_at: t.collected_at, trade_timestamp: t.trade_timestamp, fill_timestamp: t.fill_timestamp, error_reason: t.error_reason, trade_reason: t.trade_reason };
          if (isBuy) {
            if (pendingBuy) pairs.push({ buy: pendingBuy });
            pendingBuy = trade;
          } else {
            if (pendingBuy) { pairs.push({ buy: pendingBuy, sell: trade }); pendingBuy = null; }
            else pairs.push({ sell: trade });
          }
        }
        if (pendingBuy) pairs.push({ buy: pendingBuy });
        pairsByStock.set(sc, pairs);
      }
      out.set(round, pairsByStock);
    }
    return out;
  }, [btAttemptsByRound]);

  /** 종목코드 → 키움 페어 (BUY→SELL 시간순 짝지음). stk_cd의 'A' prefix 제거해서 actualTrades와 동일 키로. */
  type KiwoomRec = { id: number; ord_dt: string; ord_tm: string; stk_cd: string; stk_nm: string; io_tp_nm: string; cntr_qty: string; cntr_uv: string; ord_uv: string; comm_ord_tp: string };
  type KiwoomPair = { buy?: KiwoomRec; sell?: KiwoomRec };
  const kiwoomPairsByStock = React.useMemo(() => {
    const byStock = new Map<string, KiwoomRec[]>();
    for (const r of kiwoomRecords) {
      const sc = String(r.stk_cd ?? '').replace(/^A/, '');
      if (!sc) continue;
      if (!byStock.has(sc)) byStock.set(sc, []);
      byStock.get(sc)!.push(r);
    }
    const result = new Map<string, KiwoomPair[]>();
    for (const [sc, list] of Array.from(byStock.entries())) {
      list.sort((a, b) => {
        const k = a.ord_dt.localeCompare(b.ord_dt);
        return k !== 0 ? k : a.ord_tm.localeCompare(b.ord_tm);
      });
      const pairs: KiwoomPair[] = [];
      let pendingBuy: KiwoomRec | null = null;
      for (const r of list) {
        const isBuy = String(r.io_tp_nm ?? '').includes('매수');
        const isSell = String(r.io_tp_nm ?? '').includes('매도');
        if (isBuy) {
          if (pendingBuy) pairs.push({ buy: pendingBuy });
          pendingBuy = r;
        } else if (isSell) {
          if (pendingBuy) { pairs.push({ buy: pendingBuy, sell: r }); pendingBuy = null; }
          else pairs.push({ sell: r });
        }
      }
      if (pendingBuy) pairs.push({ buy: pendingBuy });
      result.set(sc, pairs);
    }
    return result;
  }, [kiwoomRecords]);


  /** 키움 페어 수익률 합 ((cntr_uv_sell - cntr_uv_buy) / cntr_uv_buy * 100) */
  const totalKiwoomProfit = React.useMemo(() => {
    let sum = 0;
    let pairCount = 0;
    for (const pairs of Array.from(kiwoomPairsByStock.values())) {
      for (const pair of pairs) {
        if (!pair?.buy || !pair?.sell) continue;
        const bp = parseInt(pair.buy.cntr_uv, 10) || 0;
        const sp = parseInt(pair.sell.cntr_uv, 10) || 0;
        if (bp > 0 && sp > 0) {
          sum += ((sp - bp) / bp) * 100;
          pairCount++;
        }
      }
    }
    return { total: sum, pairCount };
  }, [kiwoomPairsByStock]);

  /** "YYYY-MM-DD HH:mm:ss[.fff]" 또는 ISO 문자열 → ms (UTC) */
  const parseTradeTimeMs = React.useCallback((s: unknown): number | null => {
    if (s == null) return null;
    let n = String(s).replace(' ', 'T').trim();
    if (!n) return null;
    if (!n.endsWith('Z') && !/[+-]\d{2}:\d{2}$/.test(n)) n += 'Z';
    const ms = Date.parse(n);
    return Number.isNaN(ms) ? null : ms;
  }, []);

  /** DB 누락 의심: 페어 슬롯 단위 비교 — 같은 종목 같은 페어 인덱스에서 키움엔 leg가 있는데 actual엔 없을 때만 누락.
   *  (시간차 30초 이벤트-매칭 윈도우와 무관 — actual 시각이 늦거나 빨라도 양쪽이 같은 순서에 매수/매도가 있으면 정상.) */
  const dbMissingByActualPair = React.useMemo<Map<string, { buy: boolean; sell: boolean }>>(() => {
    const out = new Map<string, { buy: boolean; sell: boolean }>();
    const allStocks = new Set<string>();
    Array.from(kiwoomPairsByStock.keys()).forEach((sc) => allStocks.add(sc));
    Array.from(actualTradesPairsByStock.keys()).forEach((sc) => allStocks.add(sc));
    for (const sc of Array.from(allStocks)) {
      const kp = kiwoomPairsByStock.get(sc) ?? [];
      const ap = actualTradesPairsByStock.get(sc) ?? [];
      const maxTi = Math.max(kp.length, ap.length);
      for (let ti = 0; ti < maxTi; ti++) {
        const kPair = kp[ti];
        const aPair = ap[ti];
        const buyMissing = !!kPair?.buy && !aPair?.buy;
        const sellMissing = !!kPair?.sell && !aPair?.sell;
        if (buyMissing || sellMissing) {
          out.set(`${sc}|${ti}`, { buy: buyMissing, sell: sellMissing });
        }
      }
    }
    return out;
  }, [kiwoomPairsByStock, actualTradesPairsByStock]);

  /** 종목별 DB 누락 카운트 — 행 강조용 (페어 슬롯 비교 결과 기반) */
  const dbMissingSuspectByStock = React.useMemo(() => {
    const byStock = new Map<string, { buy: number; sell: number }>();
    let totalBuy = 0;
    let totalSell = 0;
    for (const [key, v] of Array.from(dbMissingByActualPair.entries())) {
      const sc = key.split('|')[0];
      const cur = byStock.get(sc) ?? { buy: 0, sell: 0 };
      if (v.buy) { cur.buy++; totalBuy++; }
      if (v.sell) { cur.sell++; totalSell++; }
      byStock.set(sc, cur);
    }
    return { byStock, totalBuy, totalSell, total: totalBuy + totalSell };
  }, [dbMissingByActualPair]);

  /** 실제 페어의 수익률 ((sell - buy) / buy * 100). order_price 우선, 없으면 체결가 price. */
  const actualProfitOfPair = React.useCallback((pair: ActualPair | undefined): number | null => {
    if (!pair?.buy || !pair?.sell) return null;
    const bp = pair.buy.order_price && pair.buy.order_price !== '' ? Number(pair.buy.order_price) : Number(pair.buy.price);
    const sp = pair.sell.order_price && pair.sell.order_price !== '' ? Number(pair.sell.order_price) : Number(pair.sell.price);
    if (!Number.isFinite(bp) || !Number.isFinite(sp) || bp === 0) return null;
    return ((sp - bp) / bp) * 100;
  }, []);

  /** 실제 거래 페어들의 수익률 합계 (회차의 totalProfit과 동일한 의미; 표의 수익률 컬럼을 모두 합산) */
  const totalActualProfit = React.useMemo(() => {
    let sum = 0;
    for (const pairs of Array.from(actualTradesPairsByStock.values())) {
      for (const pair of pairs) {
        const p = actualProfitOfPair(pair);
        if (p != null) sum += p;
      }
    }
    return sum;
  }, [actualTradesPairsByStock, actualProfitOfPair]);

  /** 시간 범위 내 데이터인지 판별 (buy_tick_id 또는 created_at 기준) */
  const isRowInTimeRange = React.useCallback(
    (row: Record<string, unknown>, startMs: number, endMs: number): boolean => {
      const tickStr = String(row.buy_tick_id ?? row.created_at ?? row.collected_at ?? '');
      if (!tickStr) return false;
      // "2026-02-26 00:00:08.787143" → "2026-02-26T00:00:08.787143Z" (UTC로 통일, startTime/endTime과 동일)
      let normalized = tickStr.replace(' ', 'T').trim();
      if (normalized && !normalized.endsWith('Z') && !normalized.match(/[+-]\d{2}:\d{2}$/)) {
        normalized = normalized + 'Z';
      }
      const rowMs = Date.parse(normalized);
      if (Number.isNaN(rowMs)) return false;
      return rowMs >= startMs && rowMs <= endMs;
    },
    []
  );

  /** 시간 범위 내 데이터: 그룹별 + (stock_code, round) -> row[] (한 종목 한 회차에 매수/매도 여러 건 허용) */
  const btResultTableData = React.useMemo(() => {
    let filtered = btResultDayTrading;
    if (selectedTimeRangeItem?.startTime && selectedTimeRangeItem?.endTime) {
      let st = selectedTimeRangeItem.startTime;
      let et = selectedTimeRangeItem.endTime;
      if (st.includes('T') && !st.includes('Z') && !st.match(/[+-]\d{2}:\d{2}$/)) st = st + 'Z';
      if (et.includes('T') && !et.includes('Z') && !et.match(/[+-]\d{2}:\d{2}$/)) et = et + 'Z';
      const startMs = Date.parse(st);
      const endMs = Date.parse(et);
      if (!Number.isNaN(startMs) && !Number.isNaN(endMs) && startMs <= endMs) {
        filtered = btResultDayTrading.filter((row) => isRowInTimeRange(row, startMs, endMs));
      }
    }

    const groups: Array<{ key: string; round: number; displayRound: number; firstCreatedAt: string; rows: Record<string, unknown>[] }> = [];
    const map = new Map<string, Record<string, unknown>[]>();
    const allStocksSet = new Set<string>();
    for (const row of filtered) {
      const sessionId = String(row.session_id ?? row.round ?? 'default');
      const sc = String(row.stock_code ?? '');
      if (sc) allStocksSet.add(sc);
      if (!map.has(sessionId)) {
        map.set(sessionId, []);
      }
      map.get(sessionId)!.push(row);
    }
    // 키움 체결·실제 거래시도에는 있는데 backtest 회차에는 없는 종목도 행으로 보여야 함 (stock 페이지와 동일하게 모두 노출)
    for (const r of kiwoomRecords) {
      const sc = String(r.stk_cd ?? '').replace(/^A/, '');
      if (sc) allStocksSet.add(sc);
    }
    for (const t of actualTrades) {
      const sc = String(t.stock_code ?? '');
      if (sc) allStocksSet.add(sc);
    }
    for (const [key, rows] of Array.from(map.entries())) {
      const first = rows[0];
      const firstCreatedAt = String(first?.created_at ?? first?.collected_at ?? '');
      const round = Number(first?.round ?? 1);
      groups.push({ key, round, displayRound: 0, firstCreatedAt, rows });
    }
    groups.sort((a, b) => a.firstCreatedAt.localeCompare(b.firstCreatedAt));
    const visibleGroups = groups.filter((g) => !deletedRoundKeys.includes(g.key));
    // 화면 표시 = 원본 round 번호 그대로 (1부터 재번호화 안 함)
    visibleGroups.forEach((g) => { g.displayRound = g.round; });

    /** (stock_code, round) -> 해당 종목 해당 회차의 매매 건 목록 (순서 유지) */
    const lookupList = new Map<string, Record<string, unknown>[]>();
    for (const g of visibleGroups) {
      for (const row of g.rows) {
        const sc = String(row.stock_code ?? '');
        if (!sc) continue;
        const k = `${sc}_${g.round}`;
        if (!lookupList.has(k)) lookupList.set(k, []);
        lookupList.get(k)!.push(row);
      }
    }

    const allStocksArray = Array.from(allStocksSet);
    /** 세로축 종목 정렬: 마지막 회차의 매수시0d0c-tick-id 순 (없으면 뒤로, 동일하면 종목번호) */
    const lastGroup = visibleGroups[visibleGroups.length - 1];
    const allStocks = lastGroup
      ? [...allStocksArray].sort((a, b) => {
          const listA = lookupList.get(`${a}_${lastGroup.round}`) ?? [];
          const listB = lookupList.get(`${b}_${lastGroup.round}`) ?? [];
          const tickA = listA.length > 0 ? String(listA[0].buy_tick_id ?? '') : '';
          const tickB = listB.length > 0 ? String(listB[0].buy_tick_id ?? '') : '';
          if (tickA && tickB) return tickA.localeCompare(tickB);
          if (tickA) return -1;
          if (tickB) return 1;
          return a.localeCompare(b);
        })
      : allStocksArray.sort();

    // maxTradesPerRound는 페이지에 보이는 종목 기준으로만 의미가 있어 렌더 시점(pagedStocks 사용)에 직접 계산.
    return { groups: visibleGroups, allStocks, lookupList };
  }, [btResultDayTrading, selectedTimeRangeItem, isRowInTimeRange, deletedRoundKeys, kiwoomRecords, actualTrades]);

  /** 행 페이지네이션 — 컴포넌트 스코프에서 계산해 maxActualPairs·렌더 양쪽에서 공유.
   *  스코프 좁히기의 핵심: 무거운 계산·렌더가 모두 pagedStocks 범위로만 돌도록. */
  const stockPagination = React.useMemo(() => {
    const all = btResultTableData.allStocks;
    const total = all.length;
    const pageCount = Math.max(1, Math.ceil(total / STOCK_PAGE_SIZE));
    const safe = Math.min(Math.max(0, stockPage), pageCount - 1);
    const start = safe * STOCK_PAGE_SIZE;
    const end = Math.min(start + STOCK_PAGE_SIZE, total);
    const slice = all.slice(start, end);
    return {
      pagedStocks: slice,
      pagedStocksSet: new Set(slice),
      stockPageCount: pageCount,
      safeStockPage: safe,
      stockStart: start,
      stockEnd: end,
      totalStockCount: total,
    };
  }, [btResultTableData.allStocks, stockPage]);

  /** 한 종목당 최대 매매 페어 수 — 페이지에 보이는 종목만 기준으로 계산해 컬럼 폭증 차단.
   *  이전엔 actualTradesPairsByStock/kiwoomPairsByStock 전체 종목 최대값을 썼는데, 비-페이지 종목이
   *  많은 페어를 가지면 보이는 5종목짜리 표가 200컬럼을 렌더하는 사고가 났음. */
  const maxActualPairs = React.useMemo(() => {
    let max = 0;
    for (const sc of stockPagination.pagedStocks) {
      const ap = actualTradesPairsByStock.get(sc);
      if (ap && ap.length > max) max = ap.length;
      const kp = kiwoomPairsByStock.get(sc);
      if (kp && kp.length > max) max = kp.length;
    }
    return Math.max(1, max);
  }, [stockPagination.pagedStocks, actualTradesPairsByStock, kiwoomPairsByStock]);

  /** 회차 row ↔ 백테 거래시도 페어 그리디 매칭 (종목 동일 + buy 시각 차 |Δt| 최소)
   *  - matchByRowKey: `${roundKey}|${stockCode}|${kth}` → 매칭 정보 (Δprofit, Δt, 귀인 태그)
   *  - summaryByRound: 회차별 집계 (Δprofit 합, 미체결 수, avgΔt)
   *  - extraTotal: 어느 회차에도 매칭되지 않은 백테 거래시도 페어 수 (완전 페어 기준)
   *  매칭 대상: bt_trade_stock (백테 주문 시도 페어 — 회차별 분리). 개발환경에서도 동작.
   */
  type RoundMatch = {
    row: Record<string, unknown>;
    pair: ActualPair;
    deltaProfit: number | null;
    deltaBuyMs: number;
    actualProfit: number | null;
    attribution: '슬리피지' | '지연매수' | '정상';
  };
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _roundActualComparison = React.useMemo(() => {
    type Cand = { roundKey: string; roundNum: number; stockCode: string; kth: number; pairIdx: number; rowMs: number; pairMs: number; absDiff: number };
    const cands: Cand[] = [];
    for (const g of btResultTableData.groups) {
      const stocksInRound = Array.from(new Set(g.rows.map((r) => String(r.stock_code ?? '')).filter(Boolean)));
      const pairsByStock = btAttemptPairsByRound.get(g.round) ?? new Map<string, ActualPair[]>();
      for (const stockCode of stocksInRound) {
        const list = btResultTableData.lookupList.get(`${stockCode}_${g.round}`) ?? [];
        const pairs = pairsByStock.get(stockCode) ?? [];
        list.forEach((row, kth) => {
          const rowMs = parseTradeTimeMs(row.buy_tick_id);
          if (rowMs == null) return;
          pairs.forEach((pair, pairIdx) => {
            if (!pair?.buy) return;
            const pairMs = parseTradeTimeMs(pair.buy.trade_timestamp ?? pair.buy.collected_at);
            if (pairMs == null) return;
            const diff = Math.abs(pairMs - rowMs);
            cands.push({ roundKey: g.key, roundNum: g.round, stockCode, kth, pairIdx, rowMs, pairMs, absDiff: diff });
          });
        });
      }
    }
    cands.sort((a, b) => a.absDiff - b.absDiff);

    const matchByRowKey = new Map<string, RoundMatch>();
    const usedRows = new Set<string>();
    const usedPairs = new Set<string>();
    const slippagePct = (priceFilled: number | undefined, priceOrdered: string | undefined): number | null => {
      const filled = Number(priceFilled);
      const ordered = priceOrdered && priceOrdered !== '' ? Number(priceOrdered) : NaN;
      if (!Number.isFinite(filled) || !Number.isFinite(ordered) || ordered === 0) return null;
      return Math.abs((filled - ordered) / ordered) * 100;
    };
    for (const c of cands) {
      const rk = `${c.roundKey}|${c.stockCode}|${c.kth}`;
      const pk = `${c.roundKey}|${c.stockCode}|${c.pairIdx}`;
      if (usedRows.has(rk) || usedPairs.has(pk)) continue;
      usedRows.add(rk); usedPairs.add(pk);
      const list = btResultTableData.lookupList.get(`${c.stockCode}_${c.roundNum}`) ?? [];
      const row = list[c.kth];
      const pair = (btAttemptPairsByRound.get(c.roundNum)?.get(c.stockCode) ?? [])[c.pairIdx];
      if (!row || !pair) continue;
      const roundProfit = Number(row.profit_rate);
      const actualProfit = actualProfitOfPair(pair);
      const deltaProfit = Number.isFinite(roundProfit) && actualProfit != null ? (actualProfit - roundProfit) : null;
      const deltaBuyMs = c.pairMs - c.rowMs;
      const buySlip = slippagePct(pair.buy?.price, pair.buy?.order_price);
      const sellSlip = slippagePct(pair.sell?.price, pair.sell?.order_price);
      let attribution: RoundMatch['attribution'] = '정상';
      if (deltaBuyMs > DELAY_BUY_THRESHOLD_MS) attribution = '지연매수';
      else if ((buySlip != null && buySlip > SLIPPAGE_THRESHOLD_PCT) || (sellSlip != null && sellSlip > SLIPPAGE_THRESHOLD_PCT)) attribution = '슬리피지';
      matchByRowKey.set(rk, { row, pair, deltaProfit, deltaBuyMs, actualProfit, attribution });
    }

    type Summary = { matched: number; missing: number; deltaProfit: number; avgDeltaMs: number | null; roundProfitMatched: number; actualProfitMatched: number };
    const summaryByRound = new Map<string, Summary>();
    for (const g of btResultTableData.groups) {
      let matched = 0, missing = 0, deltaSum = 0, dtSum = 0, dtCount = 0, rpMatched = 0, apMatched = 0;
      const stocksInRound = Array.from(new Set(g.rows.map((r) => String(r.stock_code ?? '')).filter(Boolean)));
      for (const stockCode of stocksInRound) {
        const list = btResultTableData.lookupList.get(`${stockCode}_${g.round}`) ?? [];
        for (let kth = 0; kth < list.length; kth++) {
          const row = list[kth];
          const m = matchByRowKey.get(`${g.key}|${stockCode}|${kth}`);
          if (m) {
            matched++;
            if (m.deltaProfit != null) deltaSum += m.deltaProfit;
            const rp = Number(row.profit_rate);
            if (Number.isFinite(rp)) rpMatched += rp;
            if (m.actualProfit != null) apMatched += m.actualProfit;
            dtSum += m.deltaBuyMs;
            dtCount++;
          } else {
            missing++;
          }
        }
      }
      summaryByRound.set(g.key, {
        matched, missing,
        deltaProfit: deltaSum,
        avgDeltaMs: dtCount > 0 ? dtSum / dtCount : null,
        roundProfitMatched: rpMatched,
        actualProfitMatched: apMatched,
      });
    }

    let extraTotal = 0;
    for (const g of btResultTableData.groups) {
      const pairsByStock = btAttemptPairsByRound.get(g.round) ?? new Map<string, ActualPair[]>();
      for (const [sc, pairs] of Array.from(pairsByStock.entries())) {
        for (let idx = 0; idx < pairs.length; idx++) {
          const p = pairs[idx];
          if (p?.buy && p?.sell && !usedPairs.has(`${g.key}|${sc}|${idx}`)) extraTotal++;
        }
      }
    }
    return { matchByRowKey, summaryByRound, extraTotal };
  }, [btResultTableData, btAttemptPairsByRound, actualProfitOfPair, parseTradeTimeMs]);

  const fetchSavedDetailKeys = useCallback(async (startTime?: string, endTime?: string) => {
    if (!startTime || !endTime) {
      setSavedDetailItems([]);
      return;
    }
    setSavedDetailKeysLoading(true);
    try {
      let st = startTime;
      let et = endTime;
      if (st.includes('T') && !st.includes('Z') && !st.match(/[+-]\d{2}:\d{2}$/)) st = st + 'Z';
      if (et.includes('T') && !et.includes('Z') && !et.match(/[+-]\d{2}:\d{2}$/)) et = et + 'Z';
      const url = `/mercury-backend/bt/stock-detail-keys?start_time=${encodeURIComponent(st)}&end_time=${encodeURIComponent(et)}`;
      const res = await fetch(url);
      const data = await res.json().catch(() => ({}));
      const items = data?.data?.items ?? data?.items ?? [];
      setSavedDetailItems(Array.isArray(items) ? items : []);
    } catch {
      setSavedDetailItems([]);
    } finally {
      setSavedDetailKeysLoading(false);
    }
  }, []);

  useEffect(() => {
    const start = selectedTimeRangeItem?.startTime;
    const end = selectedTimeRangeItem?.endTime;
    fetchSavedDetailKeys(start, end);
  }, [selectedTimeRangeItem?.startTime, selectedTimeRangeItem?.endTime, fetchSavedDetailKeys]);

  /** 저장된 종목 클릭: base 키만 설정, 데이터는 차트 섹션 열 때 해당 키만 조회 */
  const loadDetailByRedisKey = useCallback((redisKeyBase: string) => {
    setDetailViewError(null);
    setDetailViewBaseKey(redisKeyBase);
    setDetailViewBySection({});
    setDetailViewLoadingSections(new Set());
    setDetailViewRequestedSectionIds([]);
  }, []);

  /** 종목별 상세데이터추출 실행 (상세데이터추출 버튼과 동일 동작) */
  const runDetailExtract = useCallback(
    async (stockCode: string) => {
      const code = stockCode.trim();
      if (!code) return;
      if (!selectedTimeRange || importHistory.length === 0) {
        setDetailExtractError('먼저 시간 범위를 선택해 주세요.');
        return;
      }
      const item = importHistory.find((h) => h.id.toString() === selectedTimeRange);
      if (!item) {
        setDetailExtractError('선택된 시간 범위를 찾을 수 없습니다.');
        return;
      }
      let startTime = item.startTime;
      let endTime = item.endTime;
      if (startTime.includes('T') && !startTime.includes('Z') && !startTime.match(/[+-]\d{2}:\d{2}$/)) {
        startTime = startTime + 'Z';
      }
      if (endTime.includes('T') && !endTime.includes('Z') && !endTime.match(/[+-]\d{2}:\d{2}$/)) {
        endTime = endTime + 'Z';
      }
      setDetailExtractError(null);
      setDetailExtractMessage(null);
      setDetailExtractLoading(true);
      setExtractingStockCode(code);
      try {
        const url = `/mercury-backend/bt/stock-detail-extract?start_time=${encodeURIComponent(startTime)}&end_time=${encodeURIComponent(endTime)}&stock_code=${encodeURIComponent(code)}`;
        const res = await fetch(url, { method: 'POST' });
        const text = await res.text();
        const data = text ? (() => { try { return JSON.parse(text); } catch { return {}; } })() : {};
        if (!res.ok) {
          setDetailExtractError(data?.message || data?.error || text || `HTTP ${res.status}`);
          return;
        }
        const payload = data?.data ?? {};
        const count = payload.events_total ?? payload.events_count ?? data?.count ?? 0;
        const redisKeyBase = payload.redis_key_base ?? payload.redis_key ?? null;
        const redisKeyDisplay = redisKeyBase ? `${redisKeyBase}:*` : '-';
        setDetailExtractMessage(`저장 완료: ${count}건 (Redis 키: ${redisKeyDisplay})`);
        await fetchSavedDetailKeys(startTime, endTime);
        if (redisKeyBase) {
          loadDetailByRedisKey(redisKeyBase);
        }
        const trades = btResultTableData.groups.flatMap((g) =>
          (btResultTableData.lookupList.get(`${code}_${g.round}`) ?? []).map((r) => ({
            buy_rate: r.buy_rate, sell_rate: r.sell_rate, buy_tick_id: r.buy_tick_id, sell_tick_id: r.sell_tick_id, profit_rate: r.profit_rate,
            displayRound: g.displayRound, firstCreatedAt: g.firstCreatedAt,
          }))
        );
        setBtDetailTrades(trades.length > 0 ? trades : null);
      } catch (err) {
        setDetailExtractError(err instanceof Error ? err.message : '요청 실패');
      } finally {
        setDetailExtractLoading(false);
        setExtractingStockCode('');
      }
    },
    [selectedTimeRange, importHistory, fetchSavedDetailKeys, loadDetailByRedisKey, btResultTableData]
  );

  /** 차트 섹션 열 때 해당 Redis 키만 핀포인트 조회 */
  const loadSection = useCallback(async (baseKey: string, sectionId: string) => {
    const redisKey = `${baseKey}:${sectionId}`;
    setDetailViewLoadingSections((prev) => new Set(prev).add(sectionId));
    setDetailViewError(null);
    try {
      const res = await fetch(`/mercury-backend/bt/stock-detail?redis_key=${encodeURIComponent(redisKey)}`);
      const text = await res.text();
      let data: { data?: { events?: unknown[] }; events?: unknown[]; message?: string } = {};
      try {
        data = JSON.parse(text) as typeof data;
      } catch {
        setDetailViewError('JSON 파싱 실패');
        return;
      }
      // 404 = 해당 섹션 Redis 키 없음(데이터 없음). 에러 대신 빈 이벤트로 처리
      if (!res.ok && res.status !== 404) {
        setDetailViewError((data as { message?: string }).message || text || `HTTP ${res.status}`);
        return;
      }
      const raw = data?.data as { events?: unknown[] } | undefined;
      const events = res.status === 404 ? [] : (raw?.events ?? (data as { events?: unknown[] })?.events ?? []);
      const arr = Array.isArray(events) ? events : [];
      const list = arr.map((e: unknown) => {
        const ev = e as { t?: unknown; src?: string; data?: unknown };
        let dataObj: Record<string, unknown> = {};
        const rawData = ev?.data;
        if (rawData != null && typeof rawData === 'object' && !Array.isArray(rawData)) {
          dataObj = rawData as Record<string, unknown>;
        } else if (typeof rawData === 'string') {
          try {
            const parsed = JSON.parse(rawData) as Record<string, unknown>;
            if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) dataObj = parsed;
          } catch {
            /* ignore */
          }
        }
        return {
          t: typeof ev?.t === 'number' ? ev.t : Number(ev?.t) || 0,
          src: ev?.src,
          data: dataObj,
        };
      });
      setDetailViewBySection((prev) => {
        const next = { ...prev, [sectionId]: list };
        if (process.env.NODE_ENV !== 'production') {
          console.log('[stock-detail] section loaded:', sectionId, 'events:', list.length, 'detailViewBySection keys:', Object.keys(next));
        }
        return next;
      });
      if (process.env.NODE_ENV !== 'production' && list.length > 0 && list[0].data && typeof list[0].data === 'object') {
        const keys = Object.keys(list[0].data as Record<string, unknown>);
        console.log('[stock-detail] first event.data keys:', keys.slice(0, 15), keys.length > 15 ? '...' : '');
      }
    } catch (err) {
      setDetailViewError(err instanceof Error ? err.message : '조회 실패');
    } finally {
      setDetailViewLoadingSections((prev) => {
        const next = new Set(prev);
        next.delete(sectionId);
        return next;
      });
    }
  }, []);

  useEffect(() => {
    const fetchImportHistory = async () => {
      try {
        const response = await fetch(
          `/mercury-backend/bt/import-history?tableName=${encodeURIComponent('kiwoom_ranking_fluctuation_daily')}`
        );
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const result = await response.json();
        const histories = result.data || [];
        const formatted = histories.map((item: any) => ({
          id: item.id,
          startTime: item.start_time,
          endTime: item.end_time,
          label: `[${item.sequence_number}] ${item.start_time} ~ ${item.end_time}`,
          importTime: item.import_time,
          sequenceNumber: item.sequence_number || 0,
          isSelected: item.is_selected || false,
        }));
        setImportHistory(formatted);
        if (formatted.length > 0 && !selectedTimeRange) {
          const selected = formatted.find((h: any) => h.isSelected) || formatted[0];
          setSelectedTimeRange(selected.id.toString());
        }
      } catch {
        setImportHistory([]);
      }
    };
    fetchImportHistory();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- mount 시 1회만 히스토리 조회·기본 선택

  return (
    <div className="dashboard-page analy-page">
      <div className="analy-scale-root">
        <div className="analy-scale-inner">
      <div className="dashboard-sections">
        <div className="dashboard-section full-width">
          <div className="section-header">
            <h2>종목별상세 분석</h2>
            <span style={{ marginLeft: '12px' }}>
              <Link to="/stock">← 등락률 백테스트</Link>
            </span>
          </div>
          <div className="section-content analy-controls" style={{ width: '100%', maxWidth: 2400, margin: '0 auto', alignItems: 'stretch', overflowX: 'hidden' }}>
            <div style={{ width: '100%', maxWidth: 2400, margin: '0 auto', minWidth: 0 }}>
            <div className="analy-box" style={{ width: '100%', maxWidth: '100%', minWidth: 0, marginBottom: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef', boxSizing: 'border-box' }}>
              <h4 style={{ margin: '0 0 15px 0', color: '#495057' }}>시간 범위 선택</h4>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                <label style={{ color: '#6c757d', fontWeight: '500' }}>시간 범위:</label>
                <select
                  value={selectedTimeRange}
                  onChange={(e) => setSelectedTimeRange(e.target.value)}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #ced4da',
                    borderRadius: '4px',
                    minWidth: '280px',
                  }}
                >
                  <option value="">선택</option>
                  {importHistory.map((item) => (
                    <option key={item.id} value={item.id.toString()}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div id="print-backtest-comparison" className="analy-box" style={{ width: '100%', maxWidth: '100%', minWidth: 0, marginBottom: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef', boxSizing: 'border-box', overflow: 'hidden' }}>
              <h4 style={{ margin: '0 0 15px 0', color: '#495057' }}>실제 백테 비교</h4>
              {btResultDayTradingLoading && <span className="analy-controls-text" style={{ color: '#6c757d', marginBottom: 10, display: 'block' }}>데이터를 로드 중입니다...</span>}
              {!btResultDayTradingLoading && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
                  <button type="button" onClick={() => { fetchBtResultDayTrading(); fetchBtResultMemos(); }} style={{ padding: '6px 12px', fontSize: 12 }}>새로고침</button>
                  <span style={{ fontSize: 11, color: '#6c757d' }}>
                    메모는 입력 시 MongoDB(bt_result_memos)에 자동 저장됩니다.
                    {lastMemoSavedAt && <span style={{ marginLeft: 8 }}>마지막 저장: {lastMemoSavedAt}</span>}
                    {savedDetailKeysLoading && <span style={{ marginLeft: 8 }}>저장 목록 로딩...</span>}
                    {detailExtractLoading && <span style={{ marginLeft: 8 }}>{extractingStockCode ? `${extractingStockCode} 상세데이터추출 중...` : '상세데이터추출 중...'}</span>}
                  </span>
                </div>
                {!selectedTimeRange ? (
                  <div style={{ marginBottom: 10, color: '#6c757d', fontSize: 12 }}>위에서 시간 범위를 선택하면 해당 구간의 매매 결과가 표시됩니다.</div>
                ) : btResultDayTrading.length === 0 ? (
                  <div style={{ marginBottom: 10, color: '#6c757d', fontSize: 12 }}>데이터 없음. stock 서버에서 매수/매도 시 bt_result_day_trading 큐로 발행하면 표시됩니다.</div>
                ) : btResultTableData.groups.length === 0 ? (
                  <div style={{ marginBottom: 10, color: '#6c757d', fontSize: 12 }}>선택한 시간 범위({selectedTimeRangeItem?.startTime} ~ {selectedTimeRangeItem?.endTime})에 해당하는 데이터가 없습니다.</div>
                ) : (() => {
                const formatEst = (dateStr: string) => {
                  if (!dateStr) return '';
                  const normalized = dateStr.replace(' ', 'T').trim() + (dateStr.match(/[+-]\d{2}:\d{2}$/) ? '' : 'Z');
                  const d = new Date(normalized);
                  if (Number.isNaN(d.getTime())) return dateStr;
                  const f = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
                  const parts = f.formatToParts(d);
                  const get = (k: string) => parts.find((p) => p.type === k)?.value ?? '';
                  const y = get('year');
                  const m = String(get('month')).padStart(2, '0');
                  const day = String(get('day')).padStart(2, '0');
                  const h = String(get('hour')).padStart(2, '0');
                  const min = String(get('minute')).padStart(2, '0');
                  const s = String(get('second')).padStart(2, '0');
                  return `${y}-${m}-${day} ${h}:${min}:${s}`;
                };
                const formatProfitRate = (v: string) => {
                  if (v === '') return '';
                  const n = Number(v);
                  return Number.isFinite(n) ? n.toFixed(2) : v;
                };
                const W_STOCK = 72;
                const W_RATE = 48; // 등락률/수익률 셀 — "±12.34%" 수용
                const W_PRICE = 64; // 가격 셀 — "9,999,999" 수용
                const W_TIME_M = 104; // 시도/체결 시각 (HH:MM:SS.ffffff)
                const W_TICK = 140; // 거부사유 컬럼 (error_reason / comm_ord_tp)
                const W_REASON = 140; // 매수/매도 사유 컬럼 (trade_reason)
                const W_MEMO = 100;
                const thCell = (w: number, borderRight?: string) => ({ padding: '0 4px', height: ROW_H2, lineHeight: ROW_H2 + 'px', overflow: 'hidden', width: w, minWidth: w, maxWidth: w, fontWeight: 600, color: '#000', backgroundColor: '#f8f9fa', verticalAlign: 'middle', textAlign: 'center' as const, ...(borderRight && { borderRight }) });
                const formatTradeTimeShort = (s: string) => {
                  if (!s) return '';
                  // "2026-05-11T00:01:06.799921Z" / "2026-05-11 00:01:06.799921" / "00:01:06" 모두 HH:MM:SS.ffffff 추출
                  const m = s.match(/(\d{2}:\d{2}:\d{2}(?:\.\d+)?)/);
                  if (m) return m[1];
                  return s.replace('T', ' ').replace(/Z$/, '').replace(/[+-]\d{2}:\d{2}$/, '').trim();
                };
                const ROW_H1 = 34; // 제목 바 — 1줄 고정 + 가로 스크롤
                const ROW_H2 = 25;
                // 4-sub-row 레이아웃 — 한 종목 = 체결매수/시도매수/체결매도/시도매도 4행. 각 22px.
                const ROW_DATA = 88;
                const ROW_SUB = ROW_DATA / 4;
                const ROW_FOOTER = 80;
                const W_LABEL = 64; // 구분 컬럼 (체결매수/시도매수/체결매도/시도매도)
                // 4행 경계선: 0-1(매수 묶음 dashed), 1-2(매수→매도 진하게), 2-3(매도 묶음 dashed), 3-끝(종목 구분 solid)
                const subRowBorder = (idx: number): string =>
                  idx === 1 ? '1px solid #868e96' : idx === 3 ? '2px solid #000' : '1px dashed #ced4da';
                const getRowMeta = (stockCode: string | number) => {
                  const displayedStock = detailViewBaseKey ? (savedDetailItems.find((s) => s.redis_key === detailViewBaseKey)?.stock_code ?? detailStockCode) : detailStockCode;
                  const isHighlighted = displayedStock && String(stockCode) === String(displayedStock);
                  return { rowBg: isHighlighted ? '#ffebee' : undefined };
                };
                const sectionTableStyle: React.CSSProperties = { borderCollapse: 'collapse', fontSize: 12, color: '#000', tableLayout: 'fixed' };
                // 종목(행) 페이지네이션 — 컴포넌트 스코프 stockPagination에서 계산된 값 사용.
                const { pagedStocks, stockPageCount, safeStockPage, stockStart, stockEnd, totalStockCount } = stockPagination;
                const titleBarStyle = (textAlign: 'left' | 'center'): React.CSSProperties => ({
                  height: ROW_H1,
                  padding: '0 8px',
                  fontSize: 12,
                  fontWeight: 600,
                  color: '#000',
                  backgroundColor: '#e9ecef',
                  borderBottom: '1px solid #dee2e6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: textAlign === 'center' ? 'center' : 'flex-start',
                  flexWrap: 'nowrap',
                  gap: 4,
                  lineHeight: 1.3,
                  overflowX: 'auto',
                  overflowY: 'hidden',
                  whiteSpace: 'nowrap',
                  boxSizing: 'border-box',
                });
                return (
                <div style={{ marginBottom: 10, position: 'relative' }}>
                  {/* 종목(행) 페이지네이션 — 한 페이지에 30종목. 매수/매도 시도(컬럼)는 가로 스크롤로 모두 노출. */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, fontSize: 12, color: '#000' }}>
                    <button type="button" disabled={safeStockPage === 0} onClick={() => setStockPage((p) => Math.max(0, p - 1))} style={{ padding: '0 8px', fontSize: 12, height: 22, lineHeight: '22px', display: 'flex', alignItems: 'center' }}>◀</button>
                    <span>
                      종목 {totalStockCount === 0 ? 0 : stockStart + 1}-{stockEnd} / {totalStockCount}
                      <span style={{ marginLeft: 6, color: '#6c757d' }}>({safeStockPage + 1}/{stockPageCount}페이지)</span>
                    </span>
                    <button type="button" disabled={safeStockPage >= stockPageCount - 1} onClick={() => setStockPage((p) => Math.min(stockPageCount - 1, p + 1))} style={{ padding: '0 8px', fontSize: 12, height: 22, lineHeight: '22px', display: 'flex', alignItems: 'center' }}>▶</button>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-start', border: '1px solid #dee2e6', borderRadius: 4, overflow: 'hidden' }}>
                    {/* === 종목번호 + 구분 column (fixed, no scroll) — 한 종목 4 sub-row === */}
                    <div style={{ flexShrink: 0, borderRight: '2px solid #000' }}>
                      <div style={titleBarStyle('center')}>종목번호</div>
                      <table style={{ ...sectionTableStyle, width: W_STOCK + W_LABEL }}>
                        <colgroup>
                          <col style={{ width: W_STOCK, minWidth: W_STOCK }} />
                          <col style={{ width: W_LABEL, minWidth: W_LABEL }} />
                        </colgroup>
                        <thead>
                          <tr style={{ height: ROW_H2, borderBottom: '1px solid #dee2e6', backgroundColor: '#f8f9fa' }}>
                            <th style={{ height: ROW_H2, lineHeight: ROW_H2 + 'px', padding: '0 4px', overflow: 'hidden' }} />
                            <th style={{ height: ROW_H2, lineHeight: ROW_H2 + 'px', padding: '0 4px', overflow: 'hidden', fontWeight: 600, color: '#000' }}>구분</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pagedStocks.map((stockCode) => {
                            const { rowBg: baseRowBg } = getRowMeta(stockCode);
                            const suspect = dbMissingSuspectByStock.byStock.get(String(stockCode));
                            const rowSuspect = !!suspect && (suspect.buy > 0 || suspect.sell > 0);
                            const rowBg = rowSuspect ? '#fff3cd' : (baseRowBg ?? '#fff');
                            const stockTitle = rowSuspect ? `DB 누락 의심: 키움 체결 있으나 bt_actual_trade_stock 미기록 (매수=${suspect!.buy}, 매도=${suspect!.sell})` : undefined;
                            // 4 sub-row 라벨: 0=매수시도, 1=매수체결, 2=매도시도, 3=매도체결
                            const labels = ['매수시도', '매수체결', '매도시도', '매도체결'];
                            const labelColor = (i: number) => (i === 0 || i === 2 ? '#856404' : '#0c5460');
                            return (
                              <React.Fragment key={stockCode}>
                                {[0, 1, 2, 3].map((si) => (
                                  <tr key={si} style={{ height: ROW_SUB, borderBottom: subRowBorder(si), backgroundColor: rowBg }} title={stockTitle}>
                                    {si === 0 && (
                                      <td rowSpan={4} style={{ padding: '4px 8px', textAlign: 'center', color: '#000', height: ROW_DATA, backgroundColor: rowBg, verticalAlign: 'middle', lineHeight: 1.2, overflow: 'hidden' }}>
                                        <div>
                                          {(() => {
                                            const saved = savedDetailItems.find((s) => String(s.stock_code) === String(stockCode));
                                            if (saved) {
                                              return (
                                                <>
                                                  <span>{stockCode}</span>
                                                  {saved.stock_name && (
                                                    <div
                                                      style={{ fontSize: 11, color: '#6c757d', cursor: 'pointer', textDecoration: 'underline' }}
                                                      onClick={() => {
                                                        setDetailStockCode(stockCode);
                                                        const trades = btResultTableData.groups.flatMap((g) =>
                                                          (btResultTableData.lookupList.get(`${stockCode}_${g.round}`) ?? []).map((r) => ({
                                                            buy_rate: r.buy_rate, sell_rate: r.sell_rate, buy_tick_id: r.buy_tick_id, sell_tick_id: r.sell_tick_id, profit_rate: r.profit_rate,
                                                            displayRound: g.displayRound, firstCreatedAt: g.firstCreatedAt,
                                                          }))
                                                        );
                                                        setBtDetailTrades(trades);
                                                        loadDetailByRedisKey(saved.redis_key);
                                                      }}
                                                    >
                                                      {saved.stock_name}
                                                    </div>
                                                  )}
                                                </>
                                              );
                                            }
                                            return (
                                              <span
                                                style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                                onClick={() => runDetailExtract(stockCode)}
                                                title="클릭 시 상세데이터추출"
                                              >
                                                {stockCode}
                                              </span>
                                            );
                                          })()}
                                        </div>
                                      </td>
                                    )}
                                    <td style={{ padding: '0 6px', fontSize: 10, fontWeight: 600, color: labelColor(si), height: ROW_SUB, backgroundColor: rowBg, lineHeight: 1.1, overflow: 'hidden', whiteSpace: 'nowrap', textAlign: 'center' }}>
                                      {labels[si]}
                                    </td>
                                  </tr>
                                ))}
                              </React.Fragment>
                            );
                          })}
                          <tr style={{ height: ROW_FOOTER, borderTop: '2px solid #000', backgroundColor: '#f8f9fa' }}>
                            <td colSpan={2} style={{ padding: '4px 8px', fontWeight: 600, verticalAlign: 'top' }}>하단 메모</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {/* === 키움 실제 + mercury-stock 거래시도 통합 section — 종목당 4 sub-row === */}
                    <div style={{ flex: '1.5 1 0', minWidth: 0, borderRight: '2px solid #000' }}>
                      <div style={titleBarStyle('left')}>
                        실제
                        <span style={{ fontSize: 11, fontWeight: 400, flexShrink: 0 }}>
                          {totalKiwoomProfit.pairCount > 0 && (
                            <span title="키움 체결단가 기준 수익률 합">키움 <span style={{ color: totalKiwoomProfit.total >= 0 ? '#c0392b' : '#1565c0' }}>{totalKiwoomProfit.total >= 0 ? '+' : ''}{totalKiwoomProfit.total.toFixed(2)}%</span></span>
                          )}
                          <span style={{ marginLeft: totalKiwoomProfit.pairCount > 0 ? 8 : 0 }} title="거래시도 페어 수익률 합">시도 <span style={{ color: totalActualProfit >= 0 ? '#c0392b' : '#1565c0' }}>{totalActualProfit >= 0 ? '+' : ''}{totalActualProfit.toFixed(2)}%</span></span>
                          {dbMissingSuspectByStock.total > 0 && (
                            <span
                              style={{ marginLeft: 8, color: '#fd7e14', fontWeight: 600 }}
                              title={`키움 체결은 있는데 bt_actual_trade_stock에 기록 없음 — DB 누락 의심. 매수=${dbMissingSuspectByStock.totalBuy} 매도=${dbMissingSuspectByStock.totalSell}`}
                            >
                              ⚠️ DB누락의심 {dbMissingSuspectByStock.total}
                            </span>
                          )}
                        </span>
                      </div>
                      <div style={{ overflowX: 'auto' }}>
                      <table style={sectionTableStyle}>
                        <colgroup>
                          {Array.from({ length: maxActualPairs * 6 }).map((_, ci) => {
                            // 페어당 6컬럼: 0=등락률(W_RATE) 1=가격(W_PRICE) 2=시각(W_TIME_M) 3=수익률(W_RATE) 4=매수/매도사유(W_REASON) 5=거부사유/통신(W_TICK)
                            const mod = ci % 6;
                            const w = mod === 1 ? W_PRICE : mod === 0 || mod === 3 ? W_RATE : mod === 2 ? W_TIME_M : mod === 4 ? W_REASON : W_TICK;
                            return <col key={`u-${ci}`} style={{ width: w, minWidth: w }} />;
                          })}
                        </colgroup>
                        <thead>
                          <tr style={{ height: ROW_H2, borderBottom: '1px solid #dee2e6', backgroundColor: '#f8f9fa' }}>
                            {Array.from({ length: maxActualPairs }).map((_, ti) => {
                              const isLastPair = ti === maxActualPairs - 1;
                              return (
                                <React.Fragment key={`u-h-${ti}`}>
                                  <th style={thCell(W_RATE)} title="등락률(%) — 시도 행만. 키움 체결 행은 등락률 없음">등락률</th>
                                  <th style={thCell(W_PRICE)} title="가격 — 체결 행=체결가, 시도 행=시도 시점 가격">가격</th>
                                  <th style={thCell(W_TIME_M)} title="시도시각→체결시각 (시도만 있으면 시도시각만 표시)">시각</th>
                                  <th style={thCell(W_RATE)}>수익률</th>
                                  <th style={thCell(W_REASON)} title="매수/매도 트리거 사유 (시도 행만)">매수/매도사유</th>
                                  <th style={thCell(W_TICK, isLastPair ? undefined : '1px solid #dee2e6')} title="키움 체결 행=통신구분, 시도 행=거부 사유">거부사유/통신</th>
                                </React.Fragment>
                              );
                            })}
                          </tr>
                        </thead>
                        {pagedStocks.map((stockCode) => {
                            const { rowBg: baseRowBg } = getRowMeta(stockCode);
                            const kPairs = kiwoomPairsByStock.get(String(stockCode)) ?? [];
                            const aPairs = actualTradesPairsByStock.get(String(stockCode)) ?? [];
                            const suspect = dbMissingSuspectByStock.byStock.get(String(stockCode));
                            const rowSuspect = !!suspect && (suspect.buy > 0 || suspect.sell > 0);
                            const rowBg = rowSuspect ? '#fff3cd' : (baseRowBg ?? '#fff');
                            const stockTitle = rowSuspect ? `DB 누락 의심 (매수=${suspect!.buy}, 매도=${suspect!.sell})` : undefined;
                            return (
                              <tbody key={stockCode} style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }}>
                                {[0, 1, 2, 3].map((si) => (
                                  <tr key={si} style={{ height: ROW_SUB, borderBottom: subRowBorder(si), backgroundColor: rowBg, pageBreakInside: 'avoid', breakInside: 'avoid' }} title={stockTitle}>
                                    {Array.from({ length: maxActualPairs }).map((_, ti) => {
                                      const isLastPair = ti === maxActualPairs - 1;
                                      const kPair = kPairs[ti];
                                      const aPair = aPairs[ti];
                                      const dbMiss = dbMissingByActualPair.get(`${stockCode}|${ti}`);
                                      let valCell = ''; let tryCell = ''; let fillCell = ''; let profitCell = ''; let reasonCell = ''; let tradeReasonCell = '';
                                      let priceCell = ''; // 시도 행: 시도 시점 가격 (등락률 옆 병기)
                                      let fillMissing = false; let dbWarn = false;
                                      if (si === 0) {
                                        // 매수시도 = actual buy
                                        const b = aPair?.buy;
                                        valCell = b?.change_rate != null ? Number(b.change_rate).toFixed(2) : '';
                                        tryCell = b ? formatTradeTimeShort(b.trade_timestamp ?? b.collected_at) : '';
                                        fillCell = b?.fill_timestamp ? formatTradeTimeShort(b.fill_timestamp) : (b ? '미체결' : '');
                                        fillMissing = !!b && !b.fill_timestamp;
                                        reasonCell = (b?.error_reason || '') || (kPair?.buy?.comm_ord_tp || '');
                                        tradeReasonCell = b?.trade_reason ?? '';
                                        dbWarn = !!dbMiss?.buy && !b?.error_reason;
                                        if (b) {
                                          const p = b.order_price && b.order_price !== '' ? Number(b.order_price) : b.price;
                                          if (Number.isFinite(p) && p > 0) priceCell = p.toLocaleString();
                                        }
                                      } else if (si === 1) {
                                        // 매수체결 = 키움 buy (등락률 없음, 체결가만)
                                        const b = kPair?.buy;
                                        const bp = b ? parseInt(b.cntr_uv, 10) || 0 : 0;
                                        priceCell = bp > 0 ? bp.toLocaleString() : '';
                                        fillCell = b?.ord_tm ?? '';
                                        reasonCell = b?.comm_ord_tp ?? '';
                                      } else if (si === 2) {
                                        // 매도시도 = actual sell — 수익률은 매도 시점에 실현됨
                                        const s = aPair?.sell;
                                        valCell = s?.change_rate != null ? Number(s.change_rate).toFixed(2) : '';
                                        tryCell = s ? formatTradeTimeShort(s.trade_timestamp ?? s.collected_at) : '';
                                        fillCell = s?.fill_timestamp ? formatTradeTimeShort(s.fill_timestamp) : (s ? '미체결' : '');
                                        fillMissing = !!s && !s.fill_timestamp;
                                        reasonCell = (s?.error_reason || '') || (kPair?.sell?.comm_ord_tp || '');
                                        tradeReasonCell = s?.trade_reason ?? '';
                                        dbWarn = !!dbMiss?.sell && !s?.error_reason;
                                        if (s) {
                                          const p = s.order_price && s.order_price !== '' ? Number(s.order_price) : s.price;
                                          if (Number.isFinite(p) && p > 0) priceCell = p.toLocaleString();
                                        }
                                        const bb = aPair?.buy; const ss = aPair?.sell;
                                        if (bb && ss) {
                                          const pb = bb.order_price && bb.order_price !== '' ? Number(bb.order_price) : bb.price;
                                          const ps = ss.order_price && ss.order_price !== '' ? Number(ss.order_price) : ss.price;
                                          if (Number.isFinite(pb) && Number.isFinite(ps) && pb !== 0) profitCell = (((ps - pb) / pb) * 100).toFixed(2);
                                        }
                                      } else {
                                        // 매도체결 = 키움 sell (등락률 없음, 체결가만) — 수익률 표시
                                        const s = kPair?.sell;
                                        const sp = s ? parseInt(s.cntr_uv, 10) || 0 : 0;
                                        priceCell = sp > 0 ? sp.toLocaleString() : '';
                                        fillCell = s?.ord_tm ?? '';
                                        reasonCell = s?.comm_ord_tp ?? '';
                                        const bp = kPair?.buy ? parseInt(kPair.buy.cntr_uv, 10) || 0 : 0;
                                        if (bp > 0 && sp > 0) profitCell = (((sp - bp) / bp) * 100).toFixed(2);
                                      }
                                      const tdBase = { height: ROW_SUB, maxHeight: ROW_SUB, backgroundColor: rowBg, lineHeight: 1.1, overflow: 'hidden', padding: '0 6px', whiteSpace: 'nowrap', textAlign: 'left', verticalAlign: 'middle' } as const;
                                      return (
                                        <React.Fragment key={`u-c-${si}-${ti}`}>
                                          <td style={{ ...tdBase, fontSize: 11, color: '#000', width: W_RATE, minWidth: W_RATE, maxWidth: W_RATE }}>
                                            {valCell}
                                            {dbWarn && <span style={{ marginLeft: 2, fontSize: 9, color: '#dc3545', fontWeight: 700 }} title="DB 누락 의심: 키움 체결 있으나 거래시도 기록 없음">⚠</span>}
                                          </td>
                                          <td style={{ ...tdBase, fontSize: 11, color: '#000', width: W_PRICE, minWidth: W_PRICE, maxWidth: W_PRICE }}>{priceCell}</td>
                                          <td style={{ ...tdBase, fontSize: 10, color: fillMissing ? '#fd7e14' : '#000', width: W_TIME_M, minWidth: W_TIME_M, maxWidth: W_TIME_M }}>
                                            {tryCell || fillCell}
                                          </td>
                                          <td style={{ ...tdBase, fontSize: 11, color: profitCell === '' ? '#000' : Number(profitCell) >= 0 ? '#c0392b' : '#1565c0', width: W_RATE, minWidth: W_RATE, maxWidth: W_RATE }}>{profitCell}</td>
                                          {(si === 0 || si === 2) && (
                                            <td rowSpan={2} style={{ ...tdBase, padding: 0, position: 'relative', width: W_REASON, minWidth: W_REASON, maxWidth: W_REASON, overflow: 'hidden' }} title={tradeReasonCell || undefined}>
                                              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, overflow: 'hidden', whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.3, fontSize: 9, color: '#0c5460', padding: '2px 6px', cursor: 'pointer' }} onClick={() => { const cb = aPair?.buy; const cs = aPair?.sell; const ckb = kPair?.buy; const cks = kPair?.sell; const r0v = cb?.change_rate != null ? Number(cb.change_rate).toFixed(2) : ''; const r0p = (() => { if (!cb) return ''; const p = cb.order_price && cb.order_price !== '' ? Number(cb.order_price) : cb.price; return (Number.isFinite(p) && p > 0) ? p.toLocaleString() : ''; })(); const r0t = cb ? formatTradeTimeShort(cb.trade_timestamp ?? cb.collected_at) : ''; const r0tr = cb?.trade_reason ?? ''; const r0rsn = (cb?.error_reason || '') || (ckb?.comm_ord_tp || ''); const kbp = ckb ? (parseInt(ckb.cntr_uv, 10) || 0) : 0; const r1p = kbp > 0 ? kbp.toLocaleString() : ''; const r1t = ckb?.ord_tm ?? ''; const r1rsn = ckb?.comm_ord_tp ?? ''; const r2v = cs?.change_rate != null ? Number(cs.change_rate).toFixed(2) : ''; const r2p = (() => { if (!cs) return ''; const p = cs.order_price && cs.order_price !== '' ? Number(cs.order_price) : cs.price; return (Number.isFinite(p) && p > 0) ? p.toLocaleString() : ''; })(); const r2t = cs ? formatTradeTimeShort(cs.trade_timestamp ?? cs.collected_at) : ''; let r2pf = ''; if (cb && cs) { const pb = cb.order_price && cb.order_price !== '' ? Number(cb.order_price) : cb.price; const ps = cs.order_price && cs.order_price !== '' ? Number(cs.order_price) : cs.price; if (Number.isFinite(pb) && Number.isFinite(ps) && pb !== 0) r2pf = (((ps - pb) / pb) * 100).toFixed(2); } const r2tr = cs?.trade_reason ?? ''; const r2rsn = (cs?.error_reason || '') || (cks?.comm_ord_tp || ''); const ksp = cks ? (parseInt(cks.cntr_uv, 10) || 0) : 0; const r3p = ksp > 0 ? ksp.toLocaleString() : ''; const r3t = cks?.ord_tm ?? ''; const r3pf = kbp > 0 && ksp > 0 ? (((ksp - kbp) / kbp) * 100).toFixed(2) : ''; const r3rsn = cks?.comm_ord_tp ?? ''; const hdr = ['종목번호','구분','등락률','가격','시각','수익률','매수/매도사유','거부사유/통신'].join('\t'); const rws = [[stockCode,'매수시도',r0v,r0p,r0t,'',r0tr,r0rsn],[stockCode,'매수체결','',r1p,r1t,'',r0tr,r1rsn],[stockCode,'매도시도',r2v,r2p,r2t,r2pf,r2tr,r2rsn],[stockCode,'매도체결','',r3p,r3t,r3pf,r2tr,r3rsn]]; navigator.clipboard.writeText([hdr,...rws.map(r=>r.join('\t'))].join('\n')); }}>{tradeReasonCell}</div>
                                            </td>
                                          )}
                                          {(si === 0 || si === 2) && (
                                            <td rowSpan={2} style={{ ...tdBase, padding: 0, position: 'relative', width: W_TICK, minWidth: W_TICK, maxWidth: W_TICK, borderRight: isLastPair ? undefined : '1px solid #dee2e6', overflow: 'hidden' }} title={reasonCell || undefined}>
                                              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, overflow: 'hidden', whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.3, fontSize: 9, color: '#fd7e14', padding: '2px 6px', cursor: reasonCell ? 'pointer' : 'default' }} onClick={() => { if (reasonCell) navigator.clipboard.writeText(reasonCell); }}>{reasonCell}</div>
                                            </td>
                                          )}
                                        </React.Fragment>
                                      );
                                    })}
                                  </tr>
                                ))}
                              </tbody>
                            );
                          })}
                        <tbody>
                          <tr style={{ height: ROW_FOOTER, borderTop: '2px solid #000', backgroundColor: '#f8f9fa' }}>
                            <td colSpan={6 * maxActualPairs} style={{ padding: '4px 8px', verticalAlign: 'top' }} />
                          </tr>
                        </tbody>
                      </table>
                      </div>
                    </div>

                    {/* === 회차 sections — 종목당 4 sub-row (체결매수/시도매수/체결매도/시도매도) === */}
                    {[...btResultTableData.groups].reverse().map((g, gi) => {
                      const isLastGroup = gi === btResultTableData.groups.length - 1;
                      const attemptPairsByStock = btAttemptPairsByRound.get(g.round) ?? new Map<string, ActualPair[]>();
                      // 시도 횟수(컬럼) 최대값을 페이지에 보이는 종목만 기준으로 계산 — 비-페이지 종목 1개가
                      // 200건 시도를 가지면 모든 회차가 200컬럼을 렌더하던 사고 차단. 페이지 이동 시
                      // 회차마다의 컬럼 폭은 그 페이지에 있는 종목 기준으로 다시 결정됨.
                      let totalMaxN = 1;
                      for (const sc of pagedStocks) {
                        const list = btResultTableData.lookupList.get(`${sc}_${g.round}`) ?? [];
                        if (list.length > totalMaxN) totalMaxN = list.length;
                        const ps = attemptPairsByStock.get(sc);
                        if (ps && ps.length > totalMaxN) totalMaxN = ps.length;
                      }
                      // 매수/매도 시도(컬럼)는 페이징하지 않고 모두 렌더 — 회차 표 wrapper의 overflowX:'auto'로 가로 스크롤.
                      const maxN = totalMaxN;
                      const totalProfit = g.rows.reduce((s, r) => s + (Number(r.profit_rate) || 0), 0);
                      // 백테 거래시도 페어 수익률 합
                      let attemptTotal = 0;
                      for (const ps of Array.from(attemptPairsByStock.values())) {
                        for (const p of ps) {
                          if (p.buy && p.sell) {
                            const pb = p.buy.order_price && p.buy.order_price !== '' ? Number(p.buy.order_price) : p.buy.price;
                            const psv = p.sell.order_price && p.sell.order_price !== '' ? Number(p.sell.order_price) : p.sell.price;
                            if (Number.isFinite(pb) && Number.isFinite(psv) && pb !== 0) attemptTotal += ((psv - pb) / pb) * 100;
                          }
                        }
                      }
                      const buyStockCount = new Set(g.rows.map((r) => String(r.stock_code ?? '')).filter(Boolean)).size;
                      return (
                        <div key={g.key} style={{ flex: '1.5 1 0', minWidth: 0, borderRight: isLastGroup ? undefined : '2px solid #000' }}>
                          <div style={titleBarStyle('left')}>
                            {/* 삭제 버튼·회차번호는 flexShrink:0 — 회차가 많아 섹션이 좁아져도 항상 보이게 */}
                            <button
                              type="button"
                              onClick={() => { setRoundToDelete({ round: g.round, displayRound: g.displayRound }); setDeleteRoundError(null); }}
                              style={{ flexShrink: 0, marginRight: 6, padding: '0 6px', fontSize: 11, height: 20, lineHeight: '20px', display: 'flex', alignItems: 'center' }}
                            >삭제</button>
                            <span style={{ flexShrink: 0, fontWeight: 600 }}>{g.displayRound}회차 {formatEst(g.firstCreatedAt)}</span>
                            <span style={{ fontSize: 11, color: '#495057', flexShrink: 0 }} title="매수가 발생한 종목 수 (동일 종목 중복 제외)">{buyStockCount}종목</span>
                            <span style={{ fontSize: 11, color: '#000', flexShrink: 0 }} title="이 회차에서 한 종목 최대 매수/매도 시도 횟수 (가로 스크롤로 모두 표시)">시도 {totalMaxN}건</span>
                            <span style={{ fontSize: 11, fontWeight: 400, flexShrink: 0 }}>
                              <span title="이 회차 모든 row의 profit_rate 합 (체결 기준)">백테 <span style={{ color: totalProfit >= 0 ? '#c0392b' : '#1565c0' }}>{totalProfit >= 0 ? '+' : ''}{totalProfit.toFixed(2)}%</span></span>
                              <span style={{ marginLeft: 8 }} title="백테 거래시도 페어 수익률 합">시도 <span style={{ color: attemptTotal >= 0 ? '#c0392b' : '#1565c0' }}>{attemptTotal >= 0 ? '+' : ''}{attemptTotal.toFixed(2)}%</span></span>
                            </span>
                          </div>
                          <div style={{ overflowX: 'auto' }}>
                          <table style={sectionTableStyle}>
                            <colgroup>
                              {Array.from({ length: maxN * 6 }).map((_, ci) => {
                                // 페어당 6컬럼: 0=등락률(W_RATE) 1=가격(W_PRICE) 2=시각(W_TIME_M) 3=수익률(W_RATE) 4=매수/매도사유(W_REASON) 5=거부사유(W_TICK)
                                const mod = ci % 6;
                                const w = mod === 1 ? W_PRICE : mod === 0 || mod === 3 ? W_RATE : mod === 2 ? W_TIME_M : mod === 4 ? W_REASON : W_TICK;
                                return <col key={`${g.key}-${ci}`} style={{ width: w, minWidth: w }} />;
                              })}
                              <col key={`${g.key}-memo`} style={{ width: W_MEMO, minWidth: W_MEMO }} />
                            </colgroup>
                            <thead>
                              <tr style={{ height: ROW_H2, borderBottom: '1px solid #dee2e6', backgroundColor: '#f8f9fa' }}>
                                {Array.from({ length: maxN }).map((_, ti) => (
                                  <React.Fragment key={`bt-h-${ti}`}>
                                    <th style={thCell(W_RATE)} title="등락률 — 체결 행=백테 결과, 시도 행=거래시도">등락률</th>
                                    <th style={thCell(W_PRICE)} title="가격 — 시도 행=시도 시점 가격 (체결 행은 bt_result에 가격 없음)">가격</th>
                                    <th style={thCell(W_TIME_M)} title="시도시각→체결시각 (시도만 있으면 시도시각만 표시)">시각</th>
                                    <th style={thCell(W_RATE)}>수익률</th>
                                    <th style={thCell(W_REASON)} title="매수/매도 트리거 사유 (시도 행만)">매수/매도사유</th>
                                    <th style={thCell(W_TICK, ti < maxN - 1 ? '1px solid #dee2e6' : undefined)} title="시도 행=거부 사유 (정상 체결은 빈칸)">거부사유</th>
                                  </React.Fragment>
                                ))}
                                <th style={thCell(W_MEMO)}>메모</th>
                              </tr>
                            </thead>
                            {pagedStocks.map((stockCode) => {
                                const { rowBg } = getRowMeta(stockCode);
                                const list = btResultTableData.lookupList.get(`${stockCode}_${g.round}`) ?? [];
                                const aPairs = attemptPairsByStock.get(String(stockCode)) ?? [];
                                return (
                                  <tbody key={stockCode} style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }}>
                                    {[0, 1, 2, 3].map((si) => (
                                      <tr key={si} style={{ height: ROW_SUB, borderBottom: subRowBorder(si), backgroundColor: rowBg, pageBreakInside: 'avoid', breakInside: 'avoid' }}>
                                        {Array.from({ length: maxN }).map((_, ti) => {
                                          const isLastPair = ti === maxN - 1;
                                          const row = list[ti];
                                          const aPair = aPairs[ti];
                                          let valCell = ''; let priceCell = ''; let tryCell = ''; let fillCell = ''; let profitCell = ''; let reasonCell = ''; let tradeReasonCell = '';
                                          let fillMissing = false;
                                          if (si === 0) {
                                            // 매수시도 = bt_trade_stock buy
                                            const b = aPair?.buy;
                                            valCell = b?.change_rate != null ? Number(b.change_rate).toFixed(2) : '';
                                            tryCell = b ? formatTradeTimeShort(b.trade_timestamp ?? b.collected_at) : '';
                                            fillCell = b?.fill_timestamp ? formatTradeTimeShort(b.fill_timestamp) : (b ? '미체결' : '');
                                            fillMissing = !!b && !b.fill_timestamp;
                                            reasonCell = b?.error_reason ?? '';
                                            tradeReasonCell = b?.trade_reason ?? '';
                                            if (b) {
                                              const p = b.order_price && b.order_price !== '' ? Number(b.order_price) : b.price;
                                              if (Number.isFinite(p) && p > 0) priceCell = p.toLocaleString();
                                            }
                                          } else if (si === 1) {
                                            // 매수체결 = bt_result_day_trading buy (가격 컬럼 없음)
                                            valCell = row?.buy_rate != null && row.buy_rate !== '' ? String(row.buy_rate) : '';
                                            fillCell = row ? formatTradeTimeShort(String(row.buy_tick_id ?? '')) : '';
                                          } else if (si === 2) {
                                            // 매도시도 = bt_trade_stock sell — 수익률은 매도 시점에 실현됨
                                            const s = aPair?.sell;
                                            valCell = s?.change_rate != null ? Number(s.change_rate).toFixed(2) : '';
                                            tryCell = s ? formatTradeTimeShort(s.trade_timestamp ?? s.collected_at) : '';
                                            fillCell = s?.fill_timestamp ? formatTradeTimeShort(s.fill_timestamp) : (s ? '미체결' : '');
                                            fillMissing = !!s && !s.fill_timestamp;
                                            reasonCell = s?.error_reason ?? '';
                                            tradeReasonCell = s?.trade_reason ?? '';
                                            if (s) {
                                              const p = s.order_price && s.order_price !== '' ? Number(s.order_price) : s.price;
                                              if (Number.isFinite(p) && p > 0) priceCell = p.toLocaleString();
                                            }
                                            if (aPair?.buy && aPair?.sell) {
                                              const pb = aPair.buy.order_price && aPair.buy.order_price !== '' ? Number(aPair.buy.order_price) : aPair.buy.price;
                                              const psv = aPair.sell.order_price && aPair.sell.order_price !== '' ? Number(aPair.sell.order_price) : aPair.sell.price;
                                              if (Number.isFinite(pb) && Number.isFinite(psv) && pb !== 0) profitCell = (((psv - pb) / pb) * 100).toFixed(2);
                                            }
                                          } else {
                                            // 매도체결 = bt_result_day_trading sell (가격 컬럼 없음) — 수익률 표시 (매도 체결 시에만)
                                            const sr = row?.sell_rate;
                                            const sellTickIDStr = row?.sell_tick_id != null ? String(row.sell_tick_id) : '';
                                            const sellHappened = sellTickIDStr !== '' && sellTickIDStr !== '0';
                                            valCell = sr != null && sr !== '' && Number(sr) !== 0 ? String(sr) : '';
                                            fillCell = row ? formatTradeTimeShort(sellTickIDStr) : '';
                                            if (sellHappened) {
                                              const profitRateRaw = row?.profit_rate != null && row.profit_rate !== '' ? String(row.profit_rate) : '';
                                              profitCell = formatProfitRate(profitRateRaw);
                                            }
                                          }
                                          const tdBase = { height: ROW_SUB, maxHeight: ROW_SUB, backgroundColor: rowBg, lineHeight: 1.1, overflow: 'hidden', padding: '0 6px', whiteSpace: 'nowrap', textAlign: 'left', verticalAlign: 'middle' } as const;
                                          return (
                                            <React.Fragment key={`bt-c-${si}-${ti}`}>
                                              <td style={{ ...tdBase, fontSize: 11, color: '#000', width: W_RATE, minWidth: W_RATE, maxWidth: W_RATE }}>{valCell}</td>
                                              <td style={{ ...tdBase, fontSize: 11, color: '#000', width: W_PRICE, minWidth: W_PRICE, maxWidth: W_PRICE }}>{priceCell}</td>
                                              <td style={{ ...tdBase, fontSize: 10, color: fillMissing ? '#fd7e14' : '#000', width: W_TIME_M, minWidth: W_TIME_M, maxWidth: W_TIME_M }}>
                                                {tryCell || fillCell}
                                              </td>
                                              <td style={{ ...tdBase, fontSize: 11, color: profitCell === '' ? '#000' : Number(profitCell) >= 0 ? '#c0392b' : '#1565c0', width: W_RATE, minWidth: W_RATE, maxWidth: W_RATE }}>
                                                {profitCell}
                                              </td>
                                              {(si === 0 || si === 2) && (
                                                <td rowSpan={2} style={{ ...tdBase, padding: 0, position: 'relative', width: W_REASON, minWidth: W_REASON, maxWidth: W_REASON, overflow: 'hidden' }} title={tradeReasonCell || undefined}>
                                                  <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, overflow: 'hidden', whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.3, fontSize: 9, color: '#0c5460', padding: '2px 6px', cursor: 'pointer' }} onClick={() => { const cb = aPair?.buy; const cs = aPair?.sell; const r0v = cb?.change_rate != null ? Number(cb.change_rate).toFixed(2) : ''; const r0p = (() => { if (!cb) return ''; const p = cb.order_price && cb.order_price !== '' ? Number(cb.order_price) : cb.price; return (Number.isFinite(p) && p > 0) ? p.toLocaleString() : ''; })(); const r0t = cb ? formatTradeTimeShort(cb.trade_timestamp ?? cb.collected_at) : ''; const r0tr = cb?.trade_reason ?? ''; const r0rsn = cb?.error_reason ?? ''; const r1v = row?.buy_rate != null && row.buy_rate !== '' ? String(row.buy_rate) : ''; const r1t = row ? formatTradeTimeShort(String(row.buy_tick_id ?? '')) : ''; const r2v = cs?.change_rate != null ? Number(cs.change_rate).toFixed(2) : ''; const r2p = (() => { if (!cs) return ''; const p = cs.order_price && cs.order_price !== '' ? Number(cs.order_price) : cs.price; return (Number.isFinite(p) && p > 0) ? p.toLocaleString() : ''; })(); const r2t = cs ? formatTradeTimeShort(cs.trade_timestamp ?? cs.collected_at) : ''; let r2pf = ''; if (cb && cs) { const pb = cb.order_price && cb.order_price !== '' ? Number(cb.order_price) : cb.price; const ps = cs.order_price && cs.order_price !== '' ? Number(cs.order_price) : cs.price; if (Number.isFinite(pb) && Number.isFinite(ps) && pb !== 0) r2pf = (((ps - pb) / pb) * 100).toFixed(2); } const r2tr = cs?.trade_reason ?? ''; const r2rsn = cs?.error_reason ?? ''; const stid = row?.sell_tick_id != null ? String(row.sell_tick_id) : ''; const sHap = stid !== '' && stid !== '0'; const r3v = sHap && row?.sell_rate != null && row.sell_rate !== '' && Number(row.sell_rate) !== 0 ? String(row.sell_rate) : ''; const r3t = row ? formatTradeTimeShort(stid) : ''; const r3pf = sHap ? formatProfitRate(row?.profit_rate != null && row.profit_rate !== '' ? String(row.profit_rate) : '') : ''; const hdr = ['종목번호','구분','등락률','가격','시각','수익률','매수/매도사유','거부사유'].join('\t'); const rws = [[stockCode,'매수시도',r0v,r0p,r0t,'',r0tr,r0rsn],[stockCode,'매수체결',r1v,'',r1t,'',r0tr,''],[stockCode,'매도시도',r2v,r2p,r2t,r2pf,r2tr,r2rsn],[stockCode,'매도체결',r3v,'',r3t,r3pf,r2tr,'']]; navigator.clipboard.writeText([hdr,...rws.map(r=>r.join('\t'))].join('\n')); }}>{tradeReasonCell}</div>
                                                </td>
                                              )}
                                              {(si === 0 || si === 2) && (
                                                <td rowSpan={2} style={{ ...tdBase, padding: 0, position: 'relative', width: W_TICK, minWidth: W_TICK, maxWidth: W_TICK, borderRight: !isLastPair ? '1px solid #dee2e6' : undefined, overflow: 'hidden' }} title={reasonCell || undefined}>
                                                  <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, overflow: 'hidden', whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.3, fontSize: 9, color: '#fd7e14', padding: '2px 6px', cursor: reasonCell ? 'pointer' : 'default' }} onClick={() => { if (reasonCell) navigator.clipboard.writeText(reasonCell); }}>{reasonCell}</div>
                                                </td>
                                              )}
                                            </React.Fragment>
                                          );
                                        })}
                                        {si === 0 && (
                                          <td rowSpan={4} style={{ padding: '2px 4px', width: W_MEMO, minWidth: W_MEMO, maxWidth: W_MEMO, height: ROW_DATA, backgroundColor: rowBg, verticalAlign: 'middle', overflow: 'hidden' }}>
                                            <textarea
                                              value={btResultMemos[`${stockCode}_${g.key}`] ?? ''}
                                              onChange={(e) => setBtResultMemo(`${stockCode}_${g.key}`, e.target.value)}
                                              placeholder="메모 (자동저장)"
                                              title="메모는 MongoDB에 자동 저장됩니다."
                                              rows={3}
                                              style={{ width: '100%', height: ROW_DATA - 8, fontSize: 11, padding: 2, resize: 'none', border: '1px solid #dee2e6', borderRadius: 4, boxSizing: 'border-box', display: 'block' }}
                                            />
                                          </td>
                                        )}
                                      </tr>
                                    ))}
                                  </tbody>
                                );
                              })}
                            <tbody>
                              <tr style={{ height: ROW_FOOTER, borderTop: '2px solid #000', backgroundColor: '#f8f9fa' }}>
                                <td colSpan={6 * maxN + 1} style={{ padding: '4px 8px', verticalAlign: 'top' }}>
                                  <textarea
                                    value={btResultMemos[`table_bottom_${g.key}`] ?? ''}
                                    onChange={(e) => setBtResultMemo(`table_bottom_${g.key}`, e.target.value)}
                                    placeholder={`백테 ${g.displayRound}회차 메모 (자동저장)`}
                                    title="메모는 MongoDB에 자동 저장됩니다."
                                    rows={2}
                                    style={{ width: '100%', fontSize: 11, padding: 4, border: '1px solid #dee2e6', borderRadius: 4, boxSizing: 'border-box', resize: 'vertical' }}
                                  />
                                </td>
                              </tr>
                            </tbody>
                          </table>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {roundToDelete && (
                    <div style={{ position: 'absolute', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.4)' }} onClick={() => !deleteRoundLoading && setRoundToDelete(null)}>
                      <div style={{ backgroundColor: '#fff', padding: 24, borderRadius: 8, boxShadow: '0 4px 20px rgba(0,0,0,0.2)', minWidth: 320 }} onClick={(e) => e.stopPropagation()}>
                        <h4 style={{ margin: '0 0 16px 0', fontSize: 16 }}>백테 {roundToDelete.displayRound}회차 데이터 삭제</h4>
                        <p style={{ margin: '0 0 20px 0', color: '#495057', fontSize: 14 }}>백테 {roundToDelete.displayRound}회차 데이터를 삭제하시겠습니까? 삭제된 데이터는 복구할 수 없습니다.</p>
                        {deleteRoundError && <p style={{ margin: '0 0 12px 0', color: '#dc3545', fontSize: 13 }}>{deleteRoundError}</p>}
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                          <button type="button" disabled={deleteRoundLoading} onClick={() => setRoundToDelete(null)} style={{ padding: '8px 16px', fontSize: 14 }}>취소</button>
                          <button
                            type="button"
                            disabled={deleteRoundLoading}
                            onClick={async () => {
                              setDeleteRoundLoading(true);
                              setDeleteRoundError(null);
                              try {
                                const res = await fetch(`/mercury-backend/bt/result-day-trading?round=${roundToDelete.round}`, { method: 'DELETE' });
                                if (!res.ok) {
                                  const errText = await res.text();
                                  setDeleteRoundError(errText || `삭제 실패 (${res.status})`);
                                  return;
                                }
                                await fetchBtResultDayTrading();
                                setRoundToDelete(null);
                              } catch (e) {
                                setDeleteRoundError(e instanceof Error ? e.message : '삭제 요청 중 오류가 발생했습니다.');
                              } finally {
                                setDeleteRoundLoading(false);
                              }
                            }}
                            style={{ padding: '8px 16px', fontSize: 14 }}
                          >
                            {deleteRoundLoading ? '삭제 중...' : '확인'}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                );
                })()}
              </>
              )}
            </div>

            <div className="analy-box" style={{ width: '100%', maxWidth: '100%', minWidth: 0, marginBottom: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef', boxSizing: 'border-box' }}>
              <h4 style={{ margin: '0 0 15px 0', color: '#495057' }}>종목별 상세 데이터</h4>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                <label style={{ color: '#6c757d', fontWeight: '500' }}>종목번호:</label>
                <input
                  type="text"
                  value={detailStockCode}
                  onChange={(e) => {
                    setDetailStockCode(e.target.value);
                    setDetailExtractError(null);
                    setDetailExtractMessage(null);
                  }}
                  placeholder="예: 005930"
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #ced4da',
                    borderRadius: '4px',
                    width: '120px',
                  }}
                />
                <button
                  type="button"
                  disabled={detailExtractLoading}
                  onClick={() => {
                    const code = detailStockCode.trim();
                    if (!code) {
                      setDetailExtractError('종목번호를 입력해 주세요.');
                      return;
                    }
                    setBtDetailTrades(null);
                    runDetailExtract(code);
                  }}
                  style={{
                    padding: '8px 16px',
                    cursor: detailExtractLoading ? 'not-allowed' : 'pointer',
                  }}
                >
                  {detailExtractLoading ? `${extractingStockCode ? extractingStockCode + ' ' : ''}추출 중...` : '상세데이터추출'}
                </button>
                {savedDetailKeysLoading && <span className="analy-controls-text" style={{ color: '#6c757d' }}>저장 목록 로딩...</span>}
                {savedDetailItems
                  .filter((item) => !(btResultTableData.allStocks || []).includes(String(item.stock_code || '')))
                  .map((item) => {
                  const isLoaded = detailViewBaseKey === item.redis_key;
                  const code = item.stock_code || item.redis_key;
                  const label = `${code}(${item.stock_name || '종목명없음'})`;
                  return (
                    <button
                      key={item.redis_key}
                      type="button"
                      disabled={isLoaded}
                      onClick={() => {
                        setBtDetailTrades(null);
                        loadDetailByRedisKey(item.redis_key);
                      }}
                      style={{
                        padding: '8px 12px',
                        cursor: isLoaded ? 'not-allowed' : 'pointer',
                        opacity: isLoaded ? 0.6 : 1,
                      }}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
              {detailExtractError && (
                <div className="analy-controls-text" style={{ marginTop: '10px', color: '#dc3545' }}>{detailExtractError}</div>
              )}
              {detailExtractMessage && (
                <div className="analy-controls-text" style={{ marginTop: '10px', color: '#198754' }}>{detailExtractMessage}</div>
              )}
              {detailViewLoadingSections.size > 0 && (() => {
                const hasTotal = detailViewRequestedSectionIds.length > 0;
                const total = hasTotal ? detailViewRequestedSectionIds.length : detailViewLoadingSections.size;
                const loaded = hasTotal ? detailViewRequestedSectionIds.filter((id) => detailViewBySection[id] !== undefined).length : 0;
                const pct = hasTotal && total ? Math.round((loaded / total) * 100) : null;
                return (
                <div className="analy-controls-text" style={{ marginTop: '12px', padding: '12px', color: '#0d6efd', backgroundColor: '#e7f1ff', borderRadius: '6px' }}>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>
                    차트 섹션 로딩 중{hasTotal ? `: ${loaded}/${total} 완료` : ''}{pct != null ? ` (${pct}%)` : ` (${detailViewLoadingSections.size}개)`}
                  </div>
                  <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13 }}>
                    {Array.from(detailViewLoadingSections).map((sectionId) => (
                      <li key={sectionId}>{sectionId}</li>
                    ))}
                  </ul>
                </div>
                );
              })()}
              {detailViewError && (
                <div className="analy-controls-text" style={{ marginTop: '10px', padding: '10px', color: '#dc3545', backgroundColor: '#f8d7da', borderRadius: '6px' }}>{detailViewError}</div>
              )}

              <div style={{ marginTop: '20px', border: '1px solid #dee2e6', borderRadius: '8px', padding: '16px', backgroundColor: '#fff', minHeight: '280px', width: '100%', maxWidth: '100%', overflow: 'hidden', boxSizing: 'border-box' }}>
                <h5 style={{ margin: '0 0 12px 0', color: '#495057' }}>차트 및 엑셀 시트</h5>
                {detailViewBaseKey == null && (
                  <p className="analy-controls-text" style={{ margin: 0, color: '#6c757d' }}>
                    저장된 종목 버튼(예: 000890)을 클릭한 뒤, 차트 섹션을 열면 해당 차트의 Redis 키만 조회합니다.
                  </p>
                )}
                {detailViewBaseKey != null && (
                  <p className="analy-controls-text" style={{ margin: '0 0 8px 0', color: '#495057' }}>
                    차트별 로드: 섹션을 열 때 해당 Redis 키만 핀포인트 조회. 로드된 섹션 <strong>{Object.keys(detailViewBySection).length}개</strong>
                  </p>
                )}
                <div className="analy-charts-wrap" style={{ flex: 1, minWidth: 1400, minHeight: 960, display: 'flex', flexDirection: 'column', width: '100%', maxWidth: '100%', overflow: 'auto' }}>
                  <Orderbook0dColumnCharts
                    baseKey={detailViewBaseKey}
                    sectionData={detailViewBySection}
                    btDetailTrades={btDetailTrades}
                    onNeedSections={(ids) => setDetailViewRequestedSectionIds(ids)}
                    onNeedSection={(sectionId) => {
                      if (!detailViewBaseKey || detailViewLoadingSections.has(sectionId)) return;
                      if (detailViewBySection[sectionId] !== undefined) return;
                      loadSection(detailViewBaseKey, sectionId);
                    }}
                    chartHeight={400}
                    maxRows={100}
                    error={detailViewError}
                    allowedTimeRange={(() => {
                      if (!selectedTimeRange) return null;
                      const item = importHistory.find((h) => h.id.toString() === selectedTimeRange);
                      if (!item?.startTime || !item?.endTime) return null;
                      let st = item.startTime;
                      let et = item.endTime;
                      if (st.includes('T') && !st.includes('Z') && !st.match(/[+-]\d{2}:\d{2}$/)) st = st + 'Z';
                      if (et.includes('T') && !et.includes('Z') && !et.match(/[+-]\d{2}:\d{2}$/)) et = et + 'Z';
                      const fromMs = Date.parse(st);
                      const toMs = Date.parse(et);
                      if (Number.isNaN(fromMs) || Number.isNaN(toMs) || fromMs >= toMs) return null;
                      return { from: fromMs, to: toMs };
                    })()}
                  />
                </div>
              </div>
            </div>
            </div>
          </div>
        </div>
      </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyPage;
